/**
 * As cenas do Ranking — ultrapassagens e marcos de meta desenhados num <canvas>.
 *
 * Nada de vídeo, GIF ou Lottie: cada cena é uma função `render(t)` determinística, desenhada com a
 * foto, o nome e a cor de quem está na disputa. Sem dependências; a única coisa que vem de fora são
 * as fontes, o logo e os sons, servidos de `public/ranking-cenas/` (o parâmetro `recursos`).
 *
 *   tocarUltrapassagem(canvas, {
 *     sobe: { nome, foto, cor, pontos, vendas, antes }, cai: { nome, foto, cor, pontos, vendas },
 *                          // antes = vendas de quem sobe antes deste ciclo
 *     posicao: 3,          // posição que trocou de dono
 *     cena: 'kart',        // opcional; sem isso, sorteia sem repetir a anterior. 'coroa' é a especial
 *                          // de quem assume o 1º lugar do dia e nunca entra no sorteio
 *     som: true,           // a animação funciona igual sem som
 *     recursos: 'pasta/',  // onde estão fontes, logo e sons
 *     duracao: 'longa',    // 'longa' (~8,5 s, com a apresentação dos dois) ou 'curta' (~5 s). A
 *                          // supermeta ignora isto e usa a 'solene', de ~19 s
 *     chamada: 'x.mp3',    // áudio próprio do evento (ultrapassagem, liderança, meta…), tocado na
 *                          // abertura no lugar do apito; os efeitos sincronizados da cena continuam
 *     sinal,               // AbortSignal: tira a cena do ar e cala a trilha
 *   }) -> Promise          // resolve quando a cena saiu; aí o ranking troca as linhas
 *
 * A foto de cada um é uma só. A expressão (olhos de desenho, queixo caído, suor, fuligem, óculos) é
 * desenhada por cima dela, a partir de três pontos do rosto: `pontos: { olhos: [[x, y], [x, y]],
 * boca: [x, y] }`, em fração da foto. Quem marca os pontos é o editor de foto do Ranking
 * (`EditorDeFotoDoVendedor`); sem eles, vale o enquadramento típico de foto de perfil.
 *
 * Além das ultrapassagens, o módulo comemora os marcos de meta, mesmo sem ultrapassagem nenhuma:
 *
 *   tocarMarco(canvas, {
 *     tipo: 'individual',          // ou 'equipe'
 *     quem: { nome, foto, cor, pontos },
 *     pct: 100,                    // a faixa que a pessoa acabou de cruzar, em % da meta
 *     marcos: [50, 80, 100, 120],  // as faixas da régua: a torre tem uma luz para cada uma até a meta
 *     vendas: 55, meta: 55,
 *     equipe: [...], supermeta: false,   // só no tipo 'equipe'
 *     som, recursos, duracao, chamada, sinal,
 *   }) -> Promise
 *
 * Tom, que é regra e não gosto: a graça é de quem sobe. Nenhum texto zoa quem caiu, e não há
 * violência de verdade em cena.
 *
 * O arquivo é grande porque cada cena é um roteiro desenhado à mão; não há o que reaproveitar entre
 * um boliche e um laço além dos auxiliares do topo. Nasceu como protótipo fora do repositório e foi
 * trazido inteiro — a validação visual está descrita em `docs/COMERCIAL.md`, "Cenas do Ranking".
 */

const W = 1920, H = 1080;
// A identidade pertence à cena, não ao tema CSS: o canvas não herda variáveis de cor.
const MARCAS = {
  mov: { nome: 'MOV', logo: 'logo-mov-fibra-roxo.svg', primaria: '#5B0090', vibrante: '#9104DD', clara: 'rgb(226,196,255)', contorno: '#2A0A40', abertura: ['#7A12C4', '#2B0050'], parede: ['#F6EEFF', '#DCC6F7'], pista: ['#EDE0FF', '#D4BDF5', '#B79BE6'], piso: ['#CDB8EE', '#A98BD6'], palco: ['#DCCBF5', '#B9A0E6'], branco: '#F4ECFF', suave: '#F3E8FF', rgb: '91,0,144', rgbContorno: '42,10,64' },
  // Logo oficial: https://xtfibra.com.br/wp-content/uploads/2023/10/LOGO-XT-1.png
  xt: { nome: 'XT', logo: 'logo-xt-fibra.png', primaria: '#AE1113', vibrante: '#D9272E', clara: '#FAD7D8', contorno: '#071A38', abertura: ['#AE1113', '#071A38'], parede: ['#FFF5F5', '#F2D8DC'], pista: ['#EDF2FA', '#CAD8ED', '#9CB2D2'], piso: ['#E9CBCD', '#C68F96'], palco: ['#DCE5F3', '#9CB2D2'], branco: '#F4F7FC', suave: '#FFF0F1', rgb: '174,17,19', rgbContorno: '7,26,56' },
};
export const identidadeDasCenas = (empresa = '') => MARCAS[/^xt[ -]fibra$/i.test(String(empresa).trim()) ? 'xt' : 'mov'];
let marca = MARCAS.mov;
let ROXO = marca.primaria, VIBRANTE = marca.vibrante, LILAS = marca.clara, CONTORNO = marca.contorno;
const ESCURO = '#232222';
function aplicarMarca(identidade) {
  marca = identidade;
  ROXO = marca.primaria; VIBRANTE = marca.vibrante; LILAS = marca.clara; CONTORNO = marca.contorno;
}
const OURO = '#F2C14E', PRATA = '#C9D2DE', BRONZE = '#D98A4E', AMARELO = '#FFD54A';

// ---------- linha do tempo ----------
// As cenas são escritas na própria régua: começam em ST e não sabem o que vem antes nem depois.
// O relógio real passa por `real`/`naCena`: abertura (chama o olhar e apresenta a disputa) ->
// cena, um pouco mais lenta -> fecho (a pose final fica na tela, com confete) -> saída.
const ST = 0.7;          // começa a cena, na régua dela
const IRIS = 0.25;       // a íris começa a abrir este tanto antes de ST
const SAIDA = 0.45;      // a cena sai deslizando para cima
const DURACOES = {
  longa: { abertura: 2.9, ritmo: 1.1, fecho: 1.3, duelo: true },    // ~8,5 s
  curta: { abertura: 1.0, ritmo: 1, fecho: 0.3, duelo: false },     // ~5 s, para dia de muita troca
  // ~19 s, só para a supermeta: acontece no máximo uma vez por mês, e tem de dar tempo de a sala
  // inteira parar para olhar. A abertura dura o que dura a fanfarra dela (6,5 s), e a pose final
  // fica seis segundos na tela, com fogos e moedas que não param.
  solene: { abertura: 6.5, ritmo: 1.1, fecho: 6, duelo: true },
};
const real = (t, d) => d.abertura + (t - ST) * d.ritmo;      // instante real em que a régua da cena marca t
const naCena = (tau, d) => ST + (tau - d.abertura) / d.ritmo;

// ---------- suavização (anexo A) ----------
const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));
const prog = (t, a, b) => clamp((t - a) / (b - a));
const lerp = (a, b, p) => a + (b - a) * p;
const eOut = p => 1 - Math.pow(1 - p, 3);
const eIn = p => p * p * p;
const eInOut = p => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
const eBack = p => { const c = 1.7; return 1 + (c + 1) * Math.pow(p - 1, 3) + c * Math.pow(p - 1, 2); };
const acaso = n => { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };   // sempre o mesmo para o mesmo n
const ciclo = x => x - Math.floor(x);   // parte fracionária, também para tempo negativo

let ctx = null;
// url -> HTMLImageElement, ou null quando não carregou. Com TETO: a URL da foto é assinada e muda a
// cada geração do cache do ranking, e uma TV ligada por dias acumularia um bitmap por foto por ciclo.
const img = new Map();
const TETO_DE_IMAGENS = 40;
let logoDaCena = null, preparado = null, ultimaCena = null;

// ---------- recursos ----------
function carregar(url) {
  if (!url) return Promise.resolve(null);
  if (img.has(url)) return Promise.resolve(img.get(url));
  return new Promise(resolve => {
    const guardar = valor => {
      img.set(url, valor);
      while (img.size > TETO_DE_IMAGENS) img.delete(img.keys().next().value);
      resolve(valor);
    };
    const i = new Image();
    i.onload = () => guardar(i);
    i.onerror = () => guardar(null);   // sem foto: cai nas iniciais
    i.src = url;
  });
}
/**
 * Carrega fontes e logo antes da primeira cena. Chamado quando o painel entra em modo apresentação,
 * para a primeira ultrapassagem do dia não esperar download com a tela já trocando as linhas.
 */
export function preparar(base, empresa = '') {
  const fontes = [['Sora', 'Sora-Regular.ttf', '400'], ['Sora', 'Sora-SemiBold.ttf', '600'], ['Sora', 'Sora-Bold.ttf', '700'], ['Sora', 'Sora-ExtraBold.ttf', '800'], ['Francy', 'Francy-Regular.ttf', '400']];
  const carregarFonte = ([familia, arquivo, peso]) => {
    try { const f = new FontFace(familia, `url(${base}fontes/${arquivo})`, { weight: peso }); return f.load().then(x => document.fonts.add(x)).catch(() => {}); }
    catch (erro) { return Promise.resolve(); }
  };
  if (!preparado) preparado = Promise.all(fontes.map(carregarFonte));
  return Promise.all([preparado, carregar(`${base}${identidadeDasCenas(empresa).logo}`)]);
}

// ---------- som ----------
// Efeitos do Mixkit (os mesmos da referência). `pico` e `inicio` são onde, dentro do arquivo, está
// o ponto mais forte e o começo audível: é o que permite alinhar o som ao quadro em que a coisa
// acontece, como `trilha.py` faz ao montar o vídeo.
const SONS = {
  apito: ['615', 0.15, 0.11], transicao: ['3120', 0.09, 0.01], desliza: ['2888', 0.04, 0], boing: ['2894', 0.11, 0.01],
  pinos: ['3136', 0.96, 0.07], assobio: ['395', 0.8, 0.07], aplauso: ['422', 2.71, 0.14], acerto: ['952', 0.34, 0],
  tonto: ['746', 0.04, 0.01], motor: ['kart-motor', 0, 0], turbo: ['kart-turbo', 0.6, 0], torcida: ['torcida', 0.4, 0],
};
const volume = db => clamp(Math.pow(10, (db + 12) / 20), 0.05, 1);   // discreto, nada estourado
function tocarArquivo(url, vol, tocando) {
  try { const a = new Audio(url); a.volume = vol; tocando.push(a); const p = a.play(); if (p && p.catch) p.catch(() => {}); }
  catch (erro) { /* sem áudio a animação segue igual */ }
}
// Devolve quem cala a trilha: os que ainda não tocaram deixam de tocar, e os que estão tocando param.
function agendarSons(base, trilha, chamada) {
  const tocando = [];
  if (chamada) tocarArquivo(chamada, 0.9, tocando);
  const timers = trilha.map(([nome, instante, alinhamento, db]) => {
    const [arquivo, pico, inicio] = SONS[nome];
    const atraso = Math.max(0, instante - (alinhamento === 'pico' ? pico : inicio));
    return setTimeout(() => tocarArquivo(`${base}sons/${arquivo}.mp3`, volume(db), tocando), atraso * 1000);
  });
  return () => { timers.forEach(clearTimeout); tocando.forEach(a => { try { a.pause(); } catch (erro) { /* já parou */ } }); };
}

// ---------- texto e formas ----------
const fonte = (peso, tam, familia = 'Sora') => (familia === 'Francy' ? `${tam}px Francy, Sora, sans-serif` : `${peso} ${tam}px Sora, system-ui, sans-serif`);
function largura(txt, peso, tam, ls = -0.02, familia = 'Sora') {
  ctx.font = fonte(peso, tam, familia); ctx.letterSpacing = `${ls * tam}px`;
  const v = ctx.measureText(txt).width - ls * tam; ctx.letterSpacing = '0px'; return v;
}
function texto(txt, x, base, peso, tam, cor, { align = 'center', ls = -0.02, familia = 'Sora' } = {}) {
  const tw = largura(txt, peso, tam, ls, familia);
  ctx.font = fonte(peso, tam, familia); ctx.letterSpacing = `${ls * tam}px`; ctx.fillStyle = cor;
  ctx.fillText(txt, align === 'center' ? x - tw / 2 : align === 'right' ? x - tw : x, base);
  ctx.letterSpacing = '0px'; return tw;
}
// Nome longo encolhe para caber, em vez de vazar da tela.
const tamanhoQueCabe = (txt, peso, tam, max, ls = -0.01) => Math.floor(tam * Math.min(1, max / largura(txt, peso, tam, ls)));
// Retângulo de cantos arredondados. `roundRect` só existe a partir do Chromium 99, e o navegador
// nativo das TVs é mais velho que isso (o da LG com webOS 6 é o 79): sem o caminho de reserva, toda
// cena lançava erro ao desenhar o primeiro retângulo e nenhuma chegava à tela.
const rr = (x, y, w, h, r) => {
  ctx.beginPath();
  if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
  const raio = Math.max(0, Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2));
  ctx.moveTo(x + raio, y);
  ctx.arcTo(x + w, y, x + w, y + h, raio); ctx.arcTo(x + w, y + h, x, y + h, raio);
  ctx.arcTo(x, y + h, x, y, raio); ctx.arcTo(x, y, x + w, y, raio);
  ctx.closePath();
};
const reta = (x0, y0, x1, y1) => { ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke(); };
const iniciais = nome => String(nome || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
const corDaPosicao = pos => (pos === 1 ? OURO : pos === 2 ? PRATA : pos === 3 ? BRONZE : LILAS);

const disco = (x, y, r) => { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); };

// ---------- rosto e expressões ----------
// A foto é uma só. A expressão é desenhada por cima dela, a partir de três pontos do rosto (os
// dois olhos e a boca), em fração da foto. `cara`, tudo opcional:
//   olhos: 'susto' | 'bravo' | 'raiva' | 'chorando' | 'confuso' | 'tonto' | 'feliz' | 'estrela' |
//          'cifrao' | 'carregando' | 'piscando'   (olhos de desenho; alguns trazem o resto junto:
//          'raiva' põe o rosto vermelho, os dentes cerrados e o vapor; 'chorando', as lágrimas;
//          'confuso', as interrogações)
//   abre: 0..1 (o quanto eles já saltaram) · olhar: [dx, dy] de -1 a 1
//   queixo: 0..1 (queixo caído) · suor: 0..1 · sujo: 0..1 (fuligem) · oculos: 0..1 · brilho: 0..1
//   t: relógio, para o que se mexe sozinho
const PONTOS = { olhos: [[0.36, 0.42], [0.64, 0.42]], boca: [0.5, 0.7] };
function medidas(p, r) {
  const pt = p.pontos || PONTOS, px = ([x, y]) => [(x - 0.5) * 2 * r, (y - 0.5) * 2 * r];
  const e = pt.olhos.map(px), b = px(pt.boca);
  return { e, b, d: Math.hypot(e[1][0] - e[0][0], e[1][1] - e[0][1]) };   // d = distância entre os olhos: a escala do rosto
}
function olhosDeDesenho(m, c, tipo) {
  const abre = c.abre == null ? 1 : clamp(c.abre); if (abre <= 0.02) return;
  const base = 0.4 * m.d * eBack(abre), t = c.t || 0, [ox, oy] = c.olhar || [0, 0];
  ctx.save(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  m.e.forEach(([ex, ey], i) => {
    const fora = i ? 1 : -1;
    const re = base * (tipo === 'susto' ? 1.12 : tipo === 'confuso' ? (i ? 1.25 : 0.82) : 1), lw = Math.max(3, re * 0.15);
    const fechado = tipo === 'piscando' && ciclo(t * 0.9 + 0.13) > 0.88;
    if (tipo === 'chorando') {   // as lágrimas descem em cachoeira, por baixo do olho
      const x1 = ex + fora * re * 0.2, y1 = ey + re * 0.7, x2 = ex + fora * re * 2.0, y2 = ey + re * 5.0, onda = re * 0.3 * Math.sin(t * 9 + i);
      for (const [larg, cor] of [[re * 0.8, CONTORNO], [re * 0.52, '#8FDCFF']]) {
        ctx.strokeStyle = cor; ctx.lineWidth = larg; ctx.beginPath(); ctx.moveTo(x1, y1);
        ctx.bezierCurveTo(x1 + fora * re * 0.2, y1 + re * 2, x2 - fora * re * 0.6 + onda, y2 - re * 2.2, x2, y2); ctx.stroke();
      }
      for (let k = 0; k < 2; k++) { const v = ciclo(t * 2 + k / 2 + i * 0.3); gota(x2 + fora * re * (0.4 + 1.3 * v), y2 + re * 0.3 - re * 1.3 * Math.sin(Math.PI * v), re * 0.28 * (1 - 0.5 * v), fora * 0.6); }
    }
    ctx.beginPath();   // o branco do olho
    if (tipo === 'feliz') { ctx.save(); ctx.globalAlpha *= 0.5; disco(ex + fora * re * 0.35, ey + re * 1.45, re * 0.5); ctx.fillStyle = '#FF7A9E'; ctx.fill(); ctx.restore(); ctx.beginPath(); }   // bochecha corada
    ctx.ellipse(ex, ey, re * 0.94, re * (fechado ? 0.16 : tipo === 'feliz' ? 0.86 : 1.06), 0, 0, 7);
    ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = lw; ctx.strokeStyle = CONTORNO; ctx.stroke();
    if (fechado) return;
    const pupila = (px, py, pr) => { disco(px, py, pr); ctx.fillStyle = ESCURO; ctx.fill(); disco(px - pr * 0.3, py - pr * 0.32, pr * 0.3); ctx.fillStyle = '#fff'; ctx.fill(); };
    if (tipo === 'tonto') {   // espiral girando
      ctx.beginPath();
      for (let a = 0; a <= 13.2; a += 0.3) { const rad = re * 0.76 * a / 13.2, ang = a + t * 7 * fora; ctx.lineTo(ex + Math.cos(ang) * rad, ey + Math.sin(ang) * rad * 1.1); }
      ctx.lineWidth = lw * 0.9; ctx.strokeStyle = CONTORNO; ctx.stroke();
    } else if (tipo === 'carregando') {   // a rodinha de "carregando"
      ctx.beginPath(); ctx.arc(ex, ey, re * 0.5, t * 7, t * 7 + 4.4); ctx.lineWidth = re * 0.27; ctx.strokeStyle = VIBRANTE; ctx.stroke();
    } else if (tipo === 'estrela') {   // estrela no lugar da pupila, pulsando
      const er = re * 0.74 * (1 + 0.1 * Math.sin(t * 8 + i)); ctx.beginPath();
      for (let k = 0; k < 10; k++) { const a = k * Math.PI / 5 - Math.PI / 2 + 0.15 * Math.sin(t * 3), q = k % 2 ? er * 0.45 : er; ctx.lineTo(ex + Math.cos(a) * q, ey + Math.sin(a) * q); }
      ctx.closePath(); ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = lw * 0.7; ctx.strokeStyle = CONTORNO; ctx.stroke();
    } else if (tipo === 'cifrao') texto('$', ex, ey + re * 0.55, 800, re * 1.55, '#12925A', { ls: 0 });
    else if (tipo === 'feliz') { ctx.beginPath(); ctx.arc(ex, ey + re * 0.3, re * 0.52, Math.PI * 1.08, Math.PI * 1.92); ctx.lineWidth = re * 0.3; ctx.strokeStyle = CONTORNO; ctx.stroke(); }   // olho fechado, sorrindo
    else if (tipo === 'chorando') { pupila(ex, ey + re * 0.12, re * 0.52); disco(ex + re * 0.2, ey + re * 0.34 + re * 0.05 * Math.sin(t * 10), re * 0.12); ctx.fillStyle = '#fff'; ctx.fill(); }
    else if (tipo === 'confuso') pupila(ex + (i ? 0.4 : -0.45) * re * 0.42, ey + (i ? -0.55 : 0.5) * re * 0.46, re * 0.34);
    else pupila(ex + ox * re * 0.42, ey + oy * re * 0.46, re * (tipo === 'susto' ? 0.3 : tipo === 'raiva' ? 0.3 : 0.42));
    ctx.strokeStyle = CONTORNO;   // sobrancelhas
    if (tipo === 'bravo' || tipo === 'raiva') { ctx.lineWidth = re * (tipo === 'raiva' ? 0.56 : 0.44); reta(ex + fora * re * 1.15, ey - re * 1.3, ex - fora * re * 0.95, ey - re * (tipo === 'raiva' ? 0.25 : 0.4)); }
    if (tipo === 'susto' || (tipo === 'confuso' && i)) { ctx.lineWidth = re * 0.26; ctx.beginPath(); ctx.moveTo(ex - re * 0.7, ey - re * 1.5); ctx.quadraticCurveTo(ex, ey - re * 1.95, ex + re * 0.7, ey - re * 1.5); ctx.stroke(); }
    if (tipo === 'confuso' && !i) { ctx.lineWidth = re * 0.34; reta(ex - re * 0.85, ey - re * 1.25, ex + re * 0.85, ey - re * 1.4); }
    if (tipo === 'chorando') { ctx.lineWidth = re * 0.3; reta(ex - fora * re * 0.9, ey - re * 1.65, ex + fora * re * 0.95, ey - re * 1.2); }
  });
  ctx.restore();
}
function interrogacao(x, y, tam, rot) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.font = fonte(800, tam); ctx.letterSpacing = '0px'; ctx.lineJoin = 'round';
  const w = ctx.measureText('?').width; ctx.lineWidth = tam * 0.24; ctx.strokeStyle = '#fff'; ctx.strokeText('?', -w / 2, 0); ctx.fillStyle = VIBRANTE; ctx.fillText('?', -w / 2, 0); ctx.restore();
}
// Coroa: (x, y) é o meio da base.
function coroa(x, y, sc = 1, rot = 0) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(sc, sc); ctx.lineJoin = 'round'; ctx.strokeStyle = CONTORNO;
  ctx.beginPath(); ctx.moveTo(-70, 0); ctx.lineTo(-86, -80); ctx.lineTo(-42, -40); ctx.lineTo(0, -106); ctx.lineTo(42, -40); ctx.lineTo(86, -80); ctx.lineTo(70, 0); ctx.closePath();
  ctx.fillStyle = OURO; ctx.fill(); ctx.lineWidth = 7; ctx.stroke();
  rr(-75, -22, 150, 28, 9); ctx.fillStyle = '#E0A62A'; ctx.fill(); ctx.stroke();
  for (const [px, py] of [[-86, -80], [0, -106], [86, -80]]) { disco(px, py, 11); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 5; ctx.stroke(); }
  for (const [px, cor] of [[-40, '#E5203A'], [0, VIBRANTE], [40, '#2EBD85']]) { disco(px, -8, 8); ctx.fillStyle = cor; ctx.fill(); ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
function oculosEscuros(m, v, r) {   // descem de cima e pousam nos olhos
  if (v <= 0) return;
  const cai = eOut(clamp(v)), [e0, e1] = m.e, my = (e0[1] + e1[1]) / 2, lw = 0.42 * m.d, lh = 0.3 * m.d;
  ctx.save(); ctx.globalAlpha *= clamp(v * 5); ctx.translate(0, -(1 - cai) * 1.7 * r); ctx.rotate(-0.22 * (1 - cai));
  ctx.fillStyle = '#17131C';
  for (const [ex] of m.e) { rr(ex - lw, my - lh * 0.7, 2 * lw, lh * 1.65, lh * 0.5); ctx.fill(); }
  ctx.fillRect(e0[0], my - lh * 0.55, e1[0] - e0[0], lh * 0.36);                       // ponte
  ctx.fillRect(e0[0] - lw - 0.3 * m.d, my - lh * 0.55, 0.34 * m.d, lh * 0.3);          // hastes
  ctx.fillRect(e1[0] + lw - 0.04 * m.d, my - lh * 0.55, 0.34 * m.d, lh * 0.3);
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = lh * 0.16; ctx.lineCap = 'round';
  for (const [ex] of m.e) reta(ex - lw * 0.55, my + lh * 0.3, ex - lw * 0.1, my - lh * 0.3);
  ctx.restore();
}
function gota(x, y, tam, rot) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.beginPath(); ctx.moveTo(0, -tam * 1.6);
  ctx.bezierCurveTo(tam * 1.1, -tam * 0.1, tam * 0.9, tam, 0, tam); ctx.bezierCurveTo(-tam * 0.9, tam, -tam * 1.1, -tam * 0.1, 0, -tam * 1.6);
  ctx.fillStyle = '#8FDCFF'; ctx.fill(); ctx.lineWidth = Math.max(2.5, tam * 0.22); ctx.strokeStyle = CONTORNO; ctx.stroke(); ctx.restore();
}
function cintilar(x, y, tam) {   // brilho de quatro pontas
  if (tam < 1.5) return;
  ctx.beginPath(); for (let k = 0; k < 8; k++) { const a = k * Math.PI / 4, m = k % 2 ? tam * 0.3 : tam; ctx.lineTo(x + Math.cos(a) * m, y + Math.sin(a) * m); }
  ctx.closePath(); ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = Math.max(2.5, tam * 0.12); ctx.lineJoin = 'round'; ctx.strokeStyle = CONTORNO; ctx.stroke();
}
// Foto redonda com anel na cor do vendedor; sem foto, as iniciais (anexo B).
// Foto que não é quadrada entra pelo quadrado do meio: esticada, o rosto sairia achatado. As fotos que
// passam pelo editor do Ranking já chegam quadradas, e aí isto devolve a imagem inteira.
function quadradoCentral(imagem) {
  const larg = imagem.naturalWidth || imagem.width, alt = imagem.naturalHeight || imagem.height, lado = Math.min(larg, alt);
  return [(larg - lado) / 2, (alt - lado) / 2, lado];
}
function rosto(p, cx, cy, r, anel = 11, contorno = 5, cara = null) {
  const c = cara || {}, m = medidas(p, r), t = c.t || 0;
  const q = p.img ? clamp(c.queixo || 0) * 0.5 * m.d : 0;   // quanto o queixo desce (com foto; sem ela, a boca é desenhada)
  const bx = m.b[0], by = Math.min(m.b[1], r * 0.9), lb = Math.min(0.6 * m.d, r * 0.62), curva = 0.07 * m.d;
  // tudo o que fica da linha da boca para baixo, numa faixa da largura da boca
  const daBocaParaBaixo = dy => { ctx.beginPath(); ctx.moveTo(bx - lb, by - curva + dy); ctx.quadraticCurveTo(bx, by + curva + dy, bx + lb, by - curva + dy); ctx.lineTo(bx + lb, r + q + 80); ctx.lineTo(bx - lb, r + q + 80); ctx.closePath(); };
  const foto = dy => { if (p.img) { const [sx, sy, lado] = quadradoCentral(p.img); ctx.drawImage(p.img, sx, sy, lado, lado, -r, -r + dy, 2 * r, 2 * r); } else { ctx.fillStyle = marca.suave; ctx.fillRect(-r, -r + dy, 2 * r, 2 * r); } };
  ctx.save(); ctx.translate(cx, cy);
  if (q > 0.5) {   // o queixo que desce leva junto um pedaço do anel
    for (const [extra, cor] of [[anel + contorno, CONTORNO], [anel, p.cor]]) {
      ctx.save(); ctx.beginPath(); ctx.rect(bx - lb - extra, by, 2 * (lb + extra), r + q + extra + 10 - by); ctx.clip();
      disco(0, q, r + extra); ctx.fillStyle = cor; ctx.fill(); ctx.restore();
    }
  }
  if (contorno) { disco(0, 0, r + anel + contorno); ctx.fillStyle = CONTORNO; ctx.fill(); }
  disco(0, 0, r + anel); ctx.fillStyle = p.cor; ctx.fill();
  ctx.save(); disco(0, 0, r); ctx.clip(); foto(0);
  if (!p.img) {
    const boca = clamp(c.queixo || 0);
    texto(iniciais(p.nome), 0, r * 0.3, 800, r * 0.82, c.olhos || boca > 0.05 ? `rgba(${marca.rgb},0.2)` : ROXO, { ls: 0 });
    if (boca > 0.05) {
      const rx = lb * (0.45 + 0.3 * boca), ry = m.d * (0.05 + 0.36 * boca), cyb = by + ry * 0.25;
      ctx.save(); ctx.beginPath(); ctx.ellipse(bx, cyb, rx, ry, 0, 0, 7); ctx.fillStyle = '#4A0D24'; ctx.fill();
      ctx.lineWidth = Math.max(3, r * 0.04); ctx.strokeStyle = CONTORNO; ctx.stroke(); ctx.clip();
      ctx.fillStyle = '#FF6F91'; ctx.beginPath(); ctx.ellipse(bx, cyb + ry * 0.8, rx * 0.7, ry * 0.6, 0, 0, 7); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.fillRect(bx - rx * 0.62, cyb - ry, rx * 1.24, ry * 0.42);
      ctx.restore();
    }
  }
  ctx.restore();
  if (q > 0.5) {   // queixo caído: a parte de baixo do rosto desce e a boca aparece por dentro
    ctx.save(); daBocaParaBaixo(0); ctx.clip();
    ctx.fillStyle = '#4A0D24'; ctx.fillRect(-r, -r, 2 * r, r + q); disco(0, q, r); ctx.fill();
    ctx.fillStyle = '#FF6F91'; ctx.beginPath(); ctx.ellipse(bx, by + q + curva * 0.5, lb * 0.62, q * 0.5, 0, 0, 7); ctx.fill();   // língua
    ctx.beginPath(); ctx.rect(bx - lb * 0.72, -r, lb * 1.44, 3 * r + q); ctx.clip();                                               // dentes de cima
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2 * Math.min(q * 0.34, 0.13 * m.d);
    ctx.beginPath(); ctx.moveTo(bx - lb, by - curva); ctx.quadraticCurveTo(bx, by + curva, bx + lb, by - curva); ctx.stroke();
    ctx.restore();
    ctx.save(); daBocaParaBaixo(q); ctx.clip(); disco(0, q, r); ctx.clip(); foto(q); ctx.restore();
    ctx.save(); disco(0, q, r); ctx.clip(); ctx.strokeStyle = `rgba(${marca.rgbContorno},0.55)`; ctx.lineWidth = Math.max(2.5, r * 0.03);
    ctx.beginPath(); ctx.moveTo(bx - lb, by - curva + q); ctx.quadraticCurveTo(bx, by + curva + q, bx + lb, by - curva + q); ctx.stroke(); ctx.restore();
  }
  const sujo = clamp(c.sujo || 0);
  if (sujo > 0) {   // manchas cinza de fuligem: o rosto continua aparecendo por baixo. Sujo, nunca machucado
    ctx.save(); disco(0, 0, r); ctx.clip();
    for (let k = 0; k < 9; k++) {
      const ang = acaso(k + 3) * 6.283, dist = (0.2 + 0.62 * acaso(k + 11)) * r, mx = Math.cos(ang) * dist, my = Math.sin(ang) * dist;
      ctx.fillStyle = `rgba(${k % 2 ? '104,100,112' : '78,74,86'},${0.5 * sujo})`;
      for (let n = 0; n < 3; n++) { disco(mx + (acaso(k * 5 + n) - 0.5) * 0.22 * r, my + (acaso(k * 5 + n + 40) - 0.5) * 0.18 * r, (0.08 + 0.09 * acaso(k + n + 20)) * r); ctx.fill(); }
    }
    ctx.restore();
    for (let k = 0; k < 3; k++) { const v = ciclo(t * 0.8 + k / 3); nuvem((k - 1) * 0.5 * r + 0.1 * r * Math.sin(v * 6 + k), -r - anel - v * 0.9 * r, 0.2 * r * sujo, 0.2 + 0.8 * v, '#9A96A6', k); }   // fumacinha subindo
  }
  const forca = c.abre == null ? 1 : clamp(c.abre);
  if (c.olhos === 'raiva' && forca > 0.02) {   // rosto vermelho, dentes cerrados, vapor pelos lados e a marca de raiva
    const yd = Math.min(m.b[1], r * 0.74), ld = lb * 0.95, hd = 0.2 * m.d;
    ctx.save(); disco(0, 0, r); ctx.clip(); ctx.fillStyle = `rgba(229,32,58,${0.3 * forca})`; ctx.fillRect(-r, -r, 2 * r, 2 * r);
    rr(bx - ld, yd - hd / 2, 2 * ld, hd, hd * 0.4); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = Math.max(3, r * 0.035); ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.lineWidth = Math.max(2, r * 0.022); for (let k = -2; k <= 2; k++) reta(bx + k * ld * 0.36, yd - hd / 2, bx + k * ld * 0.36, yd + hd / 2);
    reta(bx - ld, yd, bx + ld, yd); ctx.restore();
    for (let k = 0; k < 4; k++) { const v = ciclo(t * 1.6 + k / 4), lado = k % 2 ? 1 : -1; nuvem(lado * (r + anel) * (1.02 + 0.6 * v), -r * 0.2 - r * 0.55 * v, r * 0.2 * forca, 0.1 + 0.9 * v, '#F1F0F5', k); }
    ctx.save(); ctx.translate(r * 0.62, -r * 0.84); const e = forca * (1 + 0.12 * Math.sin(t * 14)); ctx.scale(e, e); ctx.lineCap = 'round';
    for (const [larg, cor] of [[r * 0.13, CONTORNO], [r * 0.07, '#FF4D67']]) { ctx.strokeStyle = cor; ctx.lineWidth = larg; for (let k = 0; k < 4; k++) { ctx.rotate(Math.PI / 2); ctx.beginPath(); ctx.arc(r * 0.2, r * 0.2, r * 0.13, Math.PI, Math.PI * 1.5); ctx.stroke(); } }
    ctx.restore();
  }
  if (c.olhos === 'confuso' && forca > 0.02) {   // interrogações boiando em volta da cabeça
    interrogacao(-(r + anel) * 0.95, -r * 0.95 + r * 0.08 * Math.sin(t * 4), r * 0.62 * forca, -0.3);
    interrogacao((r + anel) * 1.05, -r * 0.7 + r * 0.08 * Math.sin(t * 4 + 2), r * 0.46 * forca, 0.28);
  }
  if (c.olhos || sujo > 0.4) olhosDeDesenho(m, c, c.olhos || 'piscando');
  oculosEscuros(m, c.oculos || 0, r);
  const suor = clamp(c.suor || 0);
  if (suor > 0.05) for (let k = 0; k < 3; k++) {   // gotas voando da testa
    const v = ciclo(t * 1.5 + k / 3), lado = k % 2 ? 1 : -1, ang = -Math.PI / 2 + lado * (0.6 + 0.22 * k), dist = (r + anel) * (1.02 + 0.5 * v);
    gota(Math.cos(ang) * dist, Math.sin(ang) * dist, r * 0.12 * suor * (1 - 0.6 * v), ang - Math.PI / 2);
  }
  const brilho = clamp(c.brilho || 0);
  if (brilho > 0.05) [-2.5, -0.8, 0.3].forEach((ang, k) => cintilar(Math.cos(ang) * (r + anel) * 1.32, Math.sin(ang) * (r + anel) * 1.32, r * 0.24 * brilho * (0.35 + 0.65 * Math.abs(Math.sin(t * 4 + k * 2.1)))));
  ctx.restore();
}
function medalha(x, y, rot, sc, solta, pos) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(sc, sc); ctx.lineCap = 'round';
  if (solta) {   // fita em V acima do disco
    ctx.strokeStyle = CONTORNO; ctx.lineWidth = 18; reta(0, 0, -30, -78); reta(0, 0, 30, -78); reta(-30, -78, 30, -78);
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 10; reta(0, 0, -30, -78); reta(0, 0, 30, -78); reta(-30, -78, 30, -78);
  }
  ctx.beginPath(); ctx.arc(0, 0, 36, 0, 7); ctx.fillStyle = corDaPosicao(pos); ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 26, 0, 7); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.stroke();
  texto(`${pos}º`, 2, 12, 800, pos > 9 ? 26 : 32, ESCURO, { ls: -0.03 });
  ctx.restore();
}
// Boneco "cabeção": centro do corpo em (x, cy); cabeça em (0,-161) local; pés ~ (0,+146).
function boneco(b) {
  ctx.save(); ctx.translate(b.x, b.cy); ctx.rotate(b.rot || 0);
  const q = b.q || 0; ctx.scale(1 + q, 1 - q); ctx.lineCap = 'round';
  for (const [sx, ang] of [[-24, b.pe[0]], [24, b.pe[1]]]) {   // pernas
    ctx.save(); ctx.translate(sx, 50); ctx.rotate(ang);
    ctx.strokeStyle = CONTORNO; ctx.lineWidth = 36; reta(0, 0, 0, 86);
    ctx.strokeStyle = ESCURO; ctx.lineWidth = 25; reta(0, 0, 0, 86);
    ctx.beginPath(); ctx.ellipse(0, 96, 27, 15, 0, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.restore();
  }
  rr(-55, -58, 110, 124, 38); ctx.fillStyle = VIBRANTE; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
  if (b.medalha) {
    ctx.strokeStyle = CONTORNO; ctx.lineWidth = 16; reta(-34, -58, 0, 6); reta(34, -58, 0, 6);
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 9; reta(-34, -58, 0, 6); reta(34, -58, 0, 6);
    medalha(0, 22, 0, 0.9, false, b.medalha);
  }
  for (const [sx, ang] of [[-50, b.br[0]], [50, b.br[1]]]) {   // braços com luva de desenho
    ctx.save(); ctx.translate(sx, -34); ctx.rotate(ang);
    ctx.strokeStyle = CONTORNO; ctx.lineWidth = 32; reta(0, 0, 0, 68);
    ctx.strokeStyle = VIBRANTE; ctx.lineWidth = 21; reta(0, 0, 0, 68);
    ctx.beginPath(); ctx.arc(0, 80, 19, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.restore();
  }
  ctx.translate(0, -161); ctx.rotate(b.cab || 0);
  rosto(b.p, 0, 0, 116, 11, 5, b.cara);
  ctx.restore();
}
function estrela(cx, cy, r, rot) {
  ctx.save(); ctx.translate(cx, cy); ctx.rotate(rot); ctx.beginPath();
  for (let k = 0; k < 10; k++) { const a = k * Math.PI / 5 - Math.PI / 2, m = k % 2 ? r * 0.45 : r; ctx.lineTo(Math.cos(a) * m, Math.sin(a) * m); }
  ctx.closePath(); ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = 4; ctx.strokeStyle = CONTORNO; ctx.stroke(); ctx.restore();
}
function estouro(cx, cy, t, ti) {   // estrela de impacto, formato de quadrinho
  const p = prog(t, ti, ti + 0.32); if (t < ti || p >= 1) return;
  const sc = 0.4 + 0.8 * eOut(p), a = 1 - prog(p, 0.65, 1);
  ctx.save(); ctx.globalAlpha = a; ctx.translate(cx, cy); ctx.scale(sc, sc); ctx.rotate(0.2);
  const forma = (R, r) => { ctx.beginPath(); for (let k = 0; k < 22; k++) { const ang = k * Math.PI / 11, m = k % 2 ? r : R * (1 + 0.2 * (((k * 7) % 3) - 1)); ctx.lineTo(Math.cos(ang) * m, Math.sin(ang) * m); } ctx.closePath(); };
  forma(190, 100); ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = 8; ctx.strokeStyle = CONTORNO; ctx.stroke();
  forma(105, 58); ctx.fillStyle = '#fff'; ctx.fill();
  ctx.restore();
}
function balao(txt, x, y, t, a0, a1, cor = '#E5203A') {
  if (t < a0 || t > a1) return;
  const p = prog(t, a0, a0 + 0.2), sc = 0.5 + 0.5 * eBack(p), w = Math.max(88, largura(txt, 800, 46, 0) + 56);
  ctx.save(); ctx.translate(x, y); ctx.scale(sc, sc); ctx.rotate(0.1);
  rr(-w / 2, -44, w, 88, 44); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.stroke();
  texto(txt, 0, txt === '!' ? 22 : 16, 800, txt === '!' ? 64 : 46, cor, { ls: 0 }); ctx.restore();
}
// Texto grande de impacto ("STRIKE!", "TURBO!") com a frase que explica embaixo.
function impacto(palavra, frase, t, a0, x = 700, y = 230, s = 210, largFrase = 1100) {
  if (t < a0) return;
  const p = prog(t, a0, a0 + 0.3), sc = (1 + 0.9 * (1 - eOut(p))) * (1 + 0.025 * Math.sin((t - a0) * 6) * (p >= 1));
  ctx.save(); ctx.globalAlpha = clamp(p * 4); ctx.translate(x, y); ctx.rotate(-0.05); ctx.scale(sc, sc);
  ctx.font = fonte(0, s, 'Francy'); ctx.letterSpacing = '0px';
  const w = ctx.measureText(palavra).width, by = s * 0.36;
  ctx.fillStyle = VIBRANTE; ctx.fillText(palavra, -w / 2 + 9, by + 11);
  ctx.lineJoin = 'round'; ctx.lineWidth = 22; ctx.strokeStyle = '#fff'; ctx.strokeText(palavra, -w / 2, by);
  ctx.fillStyle = ROXO; ctx.fillText(palavra, -w / 2, by);
  ctx.restore();
  const q = prog(t, a0 + 0.4, a0 + 0.75);
  if (q > 0) { ctx.save(); ctx.globalAlpha = q; const tam = tamanhoQueCabe(frase, 700, 48, largFrase); texto(frase, x, y + Math.max(s * 0.72, s * 0.36 + 84) + 24 * (1 - eOut(q)), 700, tam, CONTORNO, { ls: -0.01 }); ctx.restore(); }
}
function letreiro() {
  if (!logoDaCena) return;
  const lh = 84, lw = lh * logoDaCena.width / logoDaCena.height;
  ctx.drawImage(logoDaCena, 1790 - lw, 70, lw, lh);
}

// ================= detalhes de desenho animado =================
// Nuvem com contorno, feita de gomos. Não some por transparência: cresce, os gomos se afastam e
// encolhem cada um no seu tempo, como fumaça desenhada. `vida` vai de 0 a 1.
const GOMOS = [[0, 0, 1], [-0.8, 0.16, 0.6], [0.76, 0.2, 0.64], [-0.38, -0.5, 0.66], [0.42, -0.46, 0.58], [0.04, 0.44, 0.68]];
function nuvem(x, y, r, vida = 0.3, cor = '#F1F0F5', semente = 0) {
  if (vida <= 0 || vida >= 1 || r < 2) return;
  const cresce = eOut(prog(vida, 0, 0.25)), some = prog(vida, 0.4, 1), borda = Math.max(3.5, r * 0.07), abre = cresce * (1 + 0.55 * some);
  const gomos = GOMOS.map(([dx, dy, k], i) => [dx * r * abre, dy * r * abre, k * r * cresce * (1 - Math.pow(some, 0.7 + 0.5 * acaso(semente * 7 + i)))]).filter(g => g[2] > 1.5);
  ctx.save(); ctx.translate(x, y); ctx.rotate(semente * 1.9);
  ctx.fillStyle = CONTORNO; for (const [dx, dy, rk] of gomos) { disco(dx, dy, rk + borda); ctx.fill(); }
  ctx.fillStyle = cor; for (const [dx, dy, rk] of gomos) { disco(dx, dy, rk); ctx.fill(); }
  ctx.fillStyle = 'rgba(255,255,255,0.6)'; for (const [dx, dy, rk] of gomos) { disco(dx - rk * 0.22, dy - rk * 0.26, rk * 0.5); ctx.fill(); }
  ctx.restore();
}
// Poeira rente ao chão: `dir` = 0 espalha para os dois lados (pouso); -1 ou 1 fica para trás (arrancada).
function poeira(x, y, vida, dir = 0, cor = '#EFE4D0') {
  if (vida <= 0 || vida >= 1) return;
  const e = eOut(vida);
  for (let i = 0; i < 3; i++) nuvem(x + (dir ? dir * (30 + 44 * i) : (i - 1) * 78) * e, y - (8 + (dir ? 14 * i : i === 1 ? 26 : 6)) * e, 28 - 3 * i, vida, cor, i + 1);
}
// Confete disparado dos dois cantos de baixo: sobe, abre e cai. `u` = segundos desde a festa.
function confete(u, n = 46) {
  if (u <= 0 || u > 4) return;
  const cores = [AMARELO, '#fff', VIBRANTE, LILAS, '#FF8FC4'];
  for (let i = 0; i < n; i++) {
    const lado = i % 2 ? 1 : -1, a = acaso(i), b = acaso(i + 71), c = acaso(i + 143);
    const x = (lado < 0 ? -30 : W + 30) - lado * (260 + 900 * a) * (1 - Math.exp(-1.5 * u)) + 26 * Math.sin(u * (3 + 3 * c) + i);
    const y = H + 30 - (1250 + 650 * b) * u + 820 * u * u;
    if (y > H + 40) continue;
    ctx.save(); ctx.translate(x, y); ctx.rotate(u * (5 + 7 * c) * lado + i); ctx.scale(1, Math.cos(u * (7 + 6 * a) + i));
    ctx.fillStyle = cores[i % cores.length]; ctx.fillRect(-13, -7, 26, 14); ctx.restore();
  }
}
function nota(x, y, tam, a) {   // nota musical: alguém assobiando, distraído
  if (a <= 0.01) return;
  ctx.save(); ctx.globalAlpha *= a; ctx.translate(x, y); ctx.rotate(-0.15); ctx.fillStyle = CONTORNO;
  ctx.beginPath(); ctx.ellipse(0, 0, tam, tam * 0.72, -0.4, 0, 7); ctx.fill(); ctx.fillRect(tam * 0.68, -tam * 3, tam * 0.3, tam * 3);
  ctx.beginPath(); ctx.moveTo(tam * 0.9, -tam * 3); ctx.quadraticCurveTo(tam * 2.5, -tam * 2.6, tam * 1.9, -tam * 1.3); ctx.quadraticCurveTo(tam * 1.9, -tam * 2.1, tam * 0.9, -tam * 2.1); ctx.fill();
  ctx.restore();
}

// ================= ABERTURA: chama o olhar e apresenta a disputa =================
// Roda no relógio real, antes da cena. Movimento grande e nas bordas da tela, que é o que a visão
// periférica pega de longe; só dois clarões, para não virar pisca-pisca.
function fitaZebrada(y, h, desloc) {
  if (h < 2) return;
  const passo = h * 1.6;
  ctx.save(); ctx.beginPath(); ctx.rect(0, y, W, h); ctx.clip();
  ctx.fillStyle = AMARELO; ctx.fillRect(0, y, W, h); ctx.fillStyle = ESCURO;
  for (let x = -passo * 2 + ciclo(desloc / passo) * passo; x < W + passo; x += passo) { ctx.beginPath(); ctx.moveTo(x, y + h); ctx.lineTo(x + h * 0.8, y); ctx.lineTo(x + h * 1.6, y); ctx.lineTo(x + h * 0.8, y + h); ctx.fill(); }
  ctx.restore();
}
function abertura(tau, d, A, B, pos, cena) {
  ctx.save(); ctx.globalAlpha = clamp(tau / 0.1);
  const g = ctx.createRadialGradient(960, 540, 80, 960, 540, 1150); g.addColorStop(0, marca.abertura[0]); g.addColorStop(1, marca.abertura[1]);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  ctx.save(); ctx.translate(960, 540); ctx.rotate(tau * 0.55); ctx.fillStyle = 'rgba(255,255,255,0.075)';   // raios girando
  for (let k = 0; k < 12; k++) { ctx.rotate(Math.PI / 6); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(1500, -200); ctx.lineTo(1500, 200); ctx.closePath(); ctx.fill(); }
  ctx.restore();
  const fita = 78 * eOut(clamp(tau / 0.25));   // fitas zebradas correndo em sentidos opostos
  fitaZebrada(0, fita, tau * 560); fitaZebrada(H - fita, fita, -tau * 560);
  // título: bate no meio da tela e, se houver duelo, sobe para dar lugar aos dois
  const V0 = 0.95, sobe = d.duelo ? eInOut(prog(tau, V0 - 0.05, V0 + 0.3)) : 0;
  const bate = prog(tau, 0.06, 0.34), k = Math.exp(-Math.max(0, tau - 0.3) * 7);
  ctx.save(); ctx.globalAlpha *= clamp(bate * 4);
  ctx.translate(960 + 16 * k * Math.sin(tau * 80), lerp(540, 188, sobe) + 10 * k * Math.cos(tau * 67)); ctx.rotate(-0.045 * (1 - sobe));
  const sc = (1 + 1.7 * (1 - eOut(bate))) * lerp(1, 0.6, sobe) * (1 + 0.025 * Math.sin(tau * 9));
  ctx.font = fonte(0, 196, 'Francy'); ctx.letterSpacing = '0px';
  const palavra = cena.titulo || 'ULTRAPASSAGEM!', w = ctx.measureText(palavra).width, cabe = sc * Math.min(1, 1640 / w);
  ctx.scale(cabe, cabe); ctx.lineJoin = 'round'; ctx.lineWidth = 26;
  ctx.strokeStyle = cena.especial ? '#fff' : AMARELO; ctx.strokeText(palavra, -w / 2 + 12, 80);
  ctx.strokeStyle = CONTORNO; ctx.strokeText(palavra, -w / 2, 66);
  ctx.fillStyle = cena.especial ? AMARELO : '#fff'; ctx.fillText(palavra, -w / 2, 66);
  ctx.restore();
  if (d.duelo && tau > V0 && cena.apresenta) cena.apresenta(tau, V0);
  else if (d.duelo && tau > V0) {   // os dois entram, se encaram e dão um tranco: quem disputa, e o que está valendo
    const TB = V0 + 0.6, YC = 586, R = 180;
    const chegaA = eBack(prog(tau, V0, V0 + 0.42)), chegaB = eBack(prog(tau, V0 + 0.1, V0 + 0.52));
    const tranco = tau > TB ? Math.exp(-(tau - TB) * 9) * Math.sin((tau - TB) * 30) : 0, medo = prog(tau, TB, TB + 0.18);
    const xa = lerp(-340, 520, chegaA) + 26 * tranco, xb = lerp(W + 340, 1400, chegaB) - 26 * tranco;
    const cabeca = (p, x, rot, cara) => { ctx.save(); ctx.translate(x, YC); ctx.rotate(rot); rosto(p, 0, 0, R, 15, 7, cara); ctx.restore(); };
    cabeca(A, xa, 0.09 + 0.03 * Math.sin(tau * 9), { olhos: 'bravo', abre: prog(tau, V0 + 0.3, V0 + 0.5), olhar: [0.85, 0], t: tau });
    cabeca(B, xb, -0.04 * medo, { olhos: 'susto', abre: medo, olhar: [-0.85, 0], queixo: 0.7 * eOut(medo), suor: medo, t: tau });
    if (cena.especial) coroa(xb + 8 * medo * Math.sin(tau * 40), YC - R - 4 - 14 * medo * Math.abs(Math.sin(tau * 22)), 1.25, 0.1 * medo * Math.sin(tau * 34));   // a coroa de quem lidera, já balançando
    for (const [p, x, n, a] of [[A, xa, pos + 1, chegaA], [B, xb, pos, chegaB]]) {
      const nome = p.curto.toUpperCase(), tam = tamanhoQueCabe(nome, 800, 58, 400, 0), larg = largura(nome, 800, tam, 0) + 76;
      ctx.save(); ctx.globalAlpha *= clamp(a);
      rr(x - larg / 2, YC + R + 44, larg, 86, 43); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
      texto(nome, x, YC + R + 44 + 43 + tam * 0.36, 800, tam, ROXO, { ls: 0.02 });
      medalha(x + (p === A ? -1 : 1) * (R + 6), YC + R - 34, 0, 1.55, false, n);   // a posição que cada um ocupa agora
      ctx.restore();
    }
    const vs = prog(tau, TB - 0.05, TB + 0.2);
    if (vs > 0) {
      ctx.save(); ctx.translate(960, YC - 6); const e = 0.3 + 0.7 * eBack(vs); ctx.scale(e, e); ctx.rotate(-0.08);
      ctx.beginPath(); for (let n = 0; n < 24; n++) { const ang = n * Math.PI / 12 + tau * 0.6, rad = n % 2 ? 118 : 172; ctx.lineTo(Math.cos(ang) * rad, Math.sin(ang) * rad); }
      ctx.closePath(); ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = 9; ctx.lineJoin = 'round'; ctx.strokeStyle = CONTORNO; ctx.stroke();
      texto('VS', 0, 54, 0, 150, ROXO, { familia: 'Francy', ls: 0 });
      ctx.restore();
    }
    const vale = prog(tau, TB + 0.1, TB + 0.35);
    if (vale > 0) { ctx.save(); ctx.globalAlpha *= vale; texto(cena.valendo || `valendo o ${pos}º lugar`, 960, 950 + 20 * (1 - eOut(vale)), 800, 58, '#fff', { ls: -0.01 }); ctx.restore(); }
  }
  const clarao = Math.max(1 - tau / 0.28, 0) * 0.9, segundo = tau > 0.5 ? Math.max(1 - (tau - 0.5) / 0.3, 0) * 0.3 : 0;
  if (clarao > 0) { ctx.fillStyle = `rgba(255,255,255,${clarao})`; ctx.fillRect(0, 0, W, H); }
  if (segundo > 0) { ctx.fillStyle = `rgba(255,213,74,${segundo})`; ctx.fillRect(0, 0, W, H); }
  ctx.restore();
}

// ================= peças das comemorações de meta =================
function trofeu(x, y, sc = 1, rot = 0) {   // (x, y) = meio da base
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(sc, sc); ctx.lineJoin = 'round'; ctx.lineCap = 'round';
  for (const lado of [-1, 1]) for (const [larg, cor] of [[20, CONTORNO], [8, OURO]]) { ctx.strokeStyle = cor; ctx.lineWidth = larg; ctx.beginPath(); ctx.arc(lado * 54, -112, 26, 0, 7); ctx.stroke(); }   // alças
  ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
  ctx.fillStyle = '#E0A62A'; ctx.fillRect(-11, -66, 22, 38); ctx.strokeRect(-11, -66, 22, 38);                // haste
  ctx.beginPath(); ctx.moveTo(-60, -152); ctx.lineTo(60, -152); ctx.quadraticCurveTo(58, -74, 0, -62); ctx.quadraticCurveTo(-58, -74, -60, -152); ctx.closePath();
  ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();                                                               // taça
  rr(-66, -162, 132, 20, 9); ctx.fillStyle = '#FFDC85'; ctx.fill(); ctx.stroke();
  rr(-48, -32, 96, 32, 9); ctx.fillStyle = ROXO; ctx.fill(); ctx.stroke();                                      // base
  ctx.beginPath(); for (let k = 0; k < 10; k++) { const a = k * Math.PI / 5 - Math.PI / 2, m = k % 2 ? 8 : 19; ctx.lineTo(Math.cos(a) * m, -112 + Math.sin(a) * m); }
  ctx.closePath(); ctx.fillStyle = '#fff'; ctx.fill();
  ctx.restore();
}
function fogos(x, y, u, cor, raio = 150) {   // um estouro de fogos: riscos que abrem e afinam
  if (u <= 0 || u >= 1) return;
  const e = eOut(u);
  ctx.save(); ctx.lineCap = 'round'; ctx.strokeStyle = cor; ctx.lineWidth = Math.max(1.5, 10 * (1 - u));
  for (let k = 0; k < 14; k++) { const a = k * Math.PI / 7 + 0.2, r1 = raio * e, r0 = r1 * (0.45 + 0.4 * u); reta(x + Math.cos(a) * r0, y + Math.sin(a) * r0 + 50 * u * u, x + Math.cos(a) * r1, y + Math.sin(a) * r1 + 50 * u * u); }
  ctx.restore();
}
const queima = (s, pontos) => pontos.forEach(([x, y, comeca, cor, raio], i) => fogos(x, y, ciclo((s - comeca) / 1.5) * 1.5 / 0.9 * (s > comeca), cor || [AMARELO, VIBRANTE, '#FF8FC4'][i % 3], raio));
function moedas(u, n = 28) {   // chuva de moedas
  if (u <= 0) return;
  for (let i = 0; i < n; i++) {
    const a = acaso(i + 200), b = acaso(i + 260), v = u - a * 1.4; if (v <= 0) continue;
    const y = -40 + (480 + 360 * a) * v + 320 * v * v; if (y > H + 40) continue;
    ctx.save(); ctx.translate(70 + (W - 140) * b, y); ctx.scale(0.15 + 0.85 * Math.abs(Math.cos(v * (5 + 5 * b) + i)), 1);
    disco(0, 0, 25); ctx.fillStyle = OURO; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.stroke();
    disco(0, 0, 15); ctx.lineWidth = 3; ctx.strokeStyle = '#B9861A'; ctx.stroke(); ctx.restore();
  }
}
function raiosDourados(x, y, raio, giro, a = 0.5) {
  if (raio < 4) return;
  ctx.save(); ctx.translate(x, y); ctx.rotate(giro); ctx.fillStyle = `rgba(242,193,78,${a})`;
  for (let k = 0; k < 12; k++) { ctx.rotate(Math.PI / 6); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(raio, -raio * 0.11); ctx.lineTo(raio, raio * 0.11); ctx.closePath(); ctx.fill(); }
  ctx.restore();
}
function plaquinhaDoNome(nome, x, y, a = 1) {
  const txt = nome.toUpperCase(), tam = tamanhoQueCabe(txt, 800, 58, 400, 0), larg = largura(txt, 800, tam, 0) + 76;
  ctx.save(); ctx.globalAlpha *= clamp(a);
  rr(x - larg / 2, y, larg, 86, 43); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
  texto(txt, x, y + 43 + tam * 0.36, 800, tam, ROXO, { ls: 0.02 }); ctx.restore();
}
// Abertura de quem comemora sozinho: o rosto sobe no meio da tela, com o número que explica o marco.
function apresentaSolo(tau, V0, A, linha, olhos) {
  const chega = eBack(prog(tau, V0, V0 + 0.45)), YC = 556, R = 184, y = lerp(H + 320, YC, chega);
  raiosDourados(960, y, 430 * clamp(chega), tau * 0.8, 0.3);
  ctx.save(); ctx.translate(960, y); ctx.rotate(0.04 * Math.sin(tau * 6));
  rosto(A, 0, 0, R, 16, 7, { olhos, abre: prog(tau, V0 + 0.4, V0 + 0.6), brilho: prog(tau, V0 + 0.45, V0 + 0.75), t: tau }); ctx.restore();
  plaquinhaDoNome(A.curto, 960, y + R + 44, chega);
  const vale = prog(tau, V0 + 0.65, V0 + 0.9);
  if (vale > 0) { ctx.save(); ctx.globalAlpha *= vale; texto(linha, 960, 950 + 20 * (1 - eOut(vale)), 800, 58, '#fff', { ls: -0.01 }); ctx.restore(); }
}
// Abertura da equipe: os rostos pipocam em fila, com o placar embaixo.
function apresentaEquipe(tau, V0, gente, linha) {
  const n = gente.length, vao = Math.min(210, 1500 / Math.max(1, n)), x0 = 960 - vao * (n - 1) / 2;
  gente.forEach((p, i) => {
    const e = eBack(prog(tau, V0 + i * 0.07, V0 + 0.35 + i * 0.07)); if (e <= 0.01) return;
    ctx.save(); ctx.translate(x0 + i * vao, 520 - 14 * Math.abs(Math.sin(tau * 7 + i))); ctx.scale(e, e);
    rosto(p, 0, 0, Math.min(84, vao * 0.4), 9, 4, { olhos: i % 2 ? 'feliz' : 'estrela', abre: prog(tau, V0 + 0.5, V0 + 0.7), t: tau }); ctx.restore();
  });
  const vale = prog(tau, V0 + 0.55, V0 + 0.85);
  if (vale > 0) { ctx.save(); ctx.globalAlpha *= vale; texto(linha, 960, 830 + 24 * (1 - eOut(vale)), 800, 110, '#fff', { ls: -0.02 }); ctx.restore(); }
}

// ================= CENA "STRIKE" (boliche) =================
const CH = 860;   // linha do chão (pés)
const strike = (() => {
  const R1 = ST + 0.7;        // quem sobe para de correr e desliza
  const TI = ST + 1.15;       // impacto
  const TL = 1.154;           // tempo de voo de quem cai
  const FIM = TI + 2.55;
  const PINO = () => new Path2D('M -22 0 C -40 -28 -42 -62 -33 -92 C -26 -115 -12 -122 -12 -138 C -12 -152 -24 -162 -22 -180 C -20 -202 20 -202 22 -180 C 24 -162 12 -152 12 -138 C 12 -122 26 -115 33 -92 C 42 -62 40 -28 22 0 Z');
  let caminhoDoPino = null;
  const PINOS = [   // x, escala, atrás de quem cai?, velocidade x, velocidade y, giro
    { x: 1140, sc: 0.85, atras: true, vx: 520, vy: 2100, w: 9 },
    { x: 1365, sc: 0.85, atras: true, vx: 950, vy: 1350, w: -11 },
    { x: 1450, sc: 1.0, atras: false, vx: 1250, vy: 950, w: 13 },
  ];
  function parede() {
    const g = ctx.createLinearGradient(0, 0, 0, CH); g.addColorStop(0, marca.parede[0]); g.addColorStop(1, marca.parede[1]);
    ctx.fillStyle = g; ctx.fillRect(-60, -60, W + 120, CH + 60);
    for (const [x, y, r] of [[330, 170, 260], [1600, 140, 320], [980, 60, 200]]) {
      const rg = ctx.createRadialGradient(x, y, 0, x, y, r); rg.addColorStop(0, 'rgba(255,255,255,0.75)'); rg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = rg; ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
    }
    letreiro();
    ctx.fillStyle = ROXO; ctx.fillRect(-60, CH - 105, W + 120, 30);
    ctx.fillStyle = VIBRANTE; ctx.fillRect(-60, CH - 75, W + 120, 8);
  }
  function piso() {
    const y0 = CH - 67, g = ctx.createLinearGradient(0, y0, 0, H); g.addColorStop(0, '#E9B57A'); g.addColorStop(1, '#B9773E');
    ctx.fillStyle = g; ctx.fillRect(-60, y0, W + 120, H - y0 + 60);
    ctx.fillStyle = '#4A3A3A'; ctx.fillRect(-60, y0, W + 120, 12);   // canaleta
    ctx.strokeStyle = 'rgba(120,60,20,0.22)'; ctx.lineWidth = 2;
    let d = 14, y = y0 + 12;
    while (y < H + 40) { y += d; d *= 1.32; reta(-60, y, W + 60, y); }
    ctx.fillStyle = `rgba(${marca.rgb},0.55)`;   // setinhas da pista
    for (const x of [260, 520, 780]) { ctx.beginPath(); ctx.moveTo(x + 44, CH + 150); ctx.lineTo(x, CH + 132); ctx.lineTo(x, CH + 168); ctx.fill(); }
  }
  function pino(x, yBase, sc, rot) {
    caminhoDoPino = caminhoDoPino || PINO();
    ctx.save(); ctx.translate(x, yBase - 100 * sc); ctx.rotate(rot); ctx.scale(sc, sc); ctx.translate(0, 100);
    ctx.fillStyle = '#fff'; ctx.fill(caminhoDoPino);
    ctx.save(); ctx.clip(caminhoDoPino); ctx.fillStyle = VIBRANTE; ctx.fillRect(-45, -127, 90, 9); ctx.fillRect(-45, -112, 90, 9); ctx.restore();
    ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke(caminhoDoPino);
    ctx.restore();
  }
  const pinoEm = (pn, t) => { if (t < TI) return [pn.x, CH, 0]; const u = t - TI; return [pn.x + pn.vx * u, CH - pn.vy * u + 1300 * u * u, pn.w * u]; };
  function sombra(x, alt, w = 120) {
    const k = clamp(1 - alt / 700, 0.25, 1);
    ctx.save(); ctx.fillStyle = `rgba(70,25,0,${0.25 * k})`; ctx.beginPath(); ctx.ellipse(x, CH + 8, w * k, 15 * k, 0, 0, 7); ctx.fill(); ctx.restore();
  }
  function velocidade(x, cy, a) {
    if (a <= 0.01) return;
    ctx.save(); ctx.globalAlpha = a * 0.55; ctx.strokeStyle = VIBRANTE; ctx.lineCap = 'round'; ctx.lineWidth = 11;
    for (const [dy, L] of [[-170, 200], [-70, 290], [30, 250], [120, 170]]) reta(x - 160 - L, cy + dy, x - 160, cy + dy);
    ctx.restore();
  }
  function plaquinha(txt, x, a) {
    if (a <= 0.01) return;
    const s = tamanhoQueCabe(txt, 800, 34, 300, 0), w = largura(txt, 800, s, 0) + 48;
    ctx.save(); ctx.globalAlpha *= a; rr(x - w / 2, CH + 44, w, 58, 29); ctx.fillStyle = ROXO; ctx.fill();
    texto(txt, x, CH + 85, 800, s, '#fff', { ls: 0.02 }); ctx.restore();
  }
  function sobeEm(t, A, pos) {
    const s = t - ST, b = { p: A, medalha: t >= TI + 1.0 ? pos : 0, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3] };
    let slide = 0, x;
    if (t < R1) {
      const f = s * 24, sn = Math.sin(f);
      x = lerp(-260, 720, prog(t, ST, R1));
      b.pe = [0.6 * sn, -0.6 * sn]; b.br = [0.3 - 1.0 * sn, -0.3 + 1.0 * sn]; b.rot = 0.12;
      b.cy = CH - 155 - 16 * Math.abs(Math.sin(f));
    } else {
      if (t < TI) { const p = prog(t, R1, TI); x = lerp(720, 1090, p); slide = eOut(clamp(p / 0.45)); }
      else if (t < TI + 0.45) { x = 1090 + 60 * eOut(prog(t, TI, TI + 0.4)); slide = 1; }
      else { const p = eInOut(prog(t, TI + 0.45, TI + 0.8)); x = 1150; slide = 1 - p; }
      const up = eOut(prog(t, TI + 0.8, TI + 1.05)), wig = 0.15 * Math.sin(t * 12) * up;
      b.rot = -1.2 * slide;
      b.cy = lerp(CH - 155, CH - 58, slide) - (t > TI + 1.15 && t < TI + 2.3 ? 34 * Math.abs(Math.sin((t - TI - 1.15) * 7.5)) : 0);
      b.pe = [lerp(0.06, 0.14, slide), lerp(-0.06, -0.14, slide)];
      b.br = [lerp(lerp(0.3, 2.7, up) + wig, 2.6, slide), lerp(lerp(-0.3, -2.7, up) - wig, -0.5, slide)];
    }
    b.x = x; b.slide = slide;
    const foco = prog(t, ST + 0.1, ST + 0.3) * (1 - prog(t, TI + 0.25, TI + 0.45));   // olho decidido até o impacto
    const ganhou = prog(t, TI + 0.8, TI + 1.1);   // depois do strike, olho de cifrão
    b.cara = { olhos: ganhou > 0 ? 'cifrao' : foco > 0 ? 'bravo' : null, abre: ganhou > 0 ? ganhou : foco, olhar: [0.7, 0], brilho: ganhou, t };
    return b;
  }
  function caiEm(t, B, pos) {
    const b = { p: B, medalha: t < TI ? pos : 0, x: 1250, rot: 0, alt: 0 };
    if (t < TI) {
      const susto = eOut(prog(t, ST + 0.85, ST + 1.0));
      b.cy = CH - 155 + 3 * Math.sin(t * 3); b.rot = -0.08 * susto;
      b.pe = [0.06, -0.06]; b.br = [lerp(0.28, 2.0, susto), lerp(-0.28, -2.0, susto)];
      b.cab = 0.03 * Math.sin(t * 2.2);
      b.cara = { olhos: 'susto', abre: prog(t, ST + 0.8, ST + 0.98), olhar: [-0.85, 0.1], queixo: susto, suor: susto, t };
    } else if (t < TI + TL) {
      const u = t - TI, h = 1500 * u - 1300 * u * u;
      b.x = 1250 + 420 * u; b.cy = CH - 155 + 89 * (u / TL) - h; b.alt = h;
      b.rot = 4 * Math.PI * (1 - Math.pow(1 - u / TL, 2));
      b.pe = [0.5 + 0.2 * Math.sin(u * 20), -0.5 - 0.2 * Math.sin(u * 20)];
      b.br = [2.2 + 0.45 * Math.sin(u * 22), -2.2 - 0.45 * Math.sin(u * 22 + 1)];
      b.cara = { olhos: 'susto', queixo: 1, t };
    } else {   // sentado e tonto
      const u = t - TI - TL;
      b.x = 1250 + 420 * TL; b.cy = CH - 66;
      b.q = 0.16 * Math.exp(-u * 8) * Math.cos(u * 26);
      b.rot = 0.07 * Math.sin(u * 6); b.cab = 0.08 * Math.sin(u * 6 + 0.8);
      b.pe = [1.3, 1.45]; b.br = [0.55 + 0.1 * Math.sin(u * 5), -0.55 - 0.1 * Math.sin(u * 5)];
      b.tonto = u;
      b.cara = { olhos: 'tonto', queixo: 0.3, t };
    }
    return b;
  }
  function tonteira(b) {
    if (b.tonto == null) return;
    const a = clamp(b.tonto * 4), hx = b.x, hy = b.cy - 161 - 128;
    ctx.save(); ctx.globalAlpha = a;
    for (let k = 0; k < 3; k++) { const ang = b.tonto * 5 + k * 2 * Math.PI / 3; estrela(hx + Math.cos(ang) * 120, hy + Math.sin(ang) * 28, 22 + 4 * Math.sin(ang), ang); }
    ctx.restore();
  }
  function desenhar(t, A, B, pos) {
    let sx = 0, sy = 0;
    if (t > TI && t < TI + 0.6) { const k = Math.exp(-(t - TI) * 8); sx = 20 * k * Math.sin((t - TI) * 75); sy = 13 * k * Math.cos((t - TI) * 61); }
    ctx.save(); ctx.translate(sx, sy);
    parede(); piso();
    const a = sobeEm(t, A, pos), b = caiEm(t, B, pos);
    sombra(b.x, b.alt, b.tonto != null ? 150 : 115);
    sombra(a.x + (a.slide ? 30 : 0), 0, a.slide > 0.5 ? 170 : 115);
    for (const pn of PINOS) if (pn.atras && t < TI) pino(...pinoEm(pn, t).slice(0, 2), pn.sc, 0);
    boneco(b); tonteira(b);
    velocidade(a.x, a.cy, t < TI ? 1 : 1 - prog(t, TI, TI + 0.3));
    // poeira do deslize, que vai ficando para trás, e a do pouso de quem caiu
    for (let n = 0; n < 4; n++) { const nasce = R1 + 0.05 + n * 0.1, vida = prog(t, nasce, nasce + 0.75); nuvem(sobeEm(nasce, A, pos).x - 150, CH - 24 - 46 * vida, 36 - 3 * n, vida, '#F0E2CC', n); }
    poeira(1250 + 420 * TL, CH - 8, prog(t, TI + TL, TI + TL + 0.6));
    boneco(a);
    for (const pn of PINOS) if (!pn.atras || t >= TI) { const [x, y, r] = pinoEm(pn, t); if (y < H + 300 && x < W + 300) pino(x, y, pn.sc, r); }
    if (t >= TI && t < TI + 1.0) {   // a medalha da posição voa de quem cai para quem sobe
      const u = (t - TI) / 1.0, alvo = sobeEm(TI + 1.0, A, pos);
      medalha(lerp(1250, alvo.x, u), lerp(CH - 155 + 22, alvo.cy + 22, u) - 430 * Math.sin(Math.PI * u), u * 6 * Math.PI, 0.95, true, pos);
    }
    estouro(1225, CH - 80, t, TI);
    balao('!', 1365, 410, t, ST + 0.85, TI);
    plaquinha(A.curto.toUpperCase(), a.x, t < R1 ? prog(t, ST + 0.15, ST + 0.35) : 1);
    plaquinha(B.curto.toUpperCase(), b.x, t < TI ? 1 : prog(t, TI + TL + 0.1, TI + TL + 0.35));
    impacto('STRIKE!', `${A.curto} passou ${B.curto}!`, t, TI + 0.3);
    ctx.restore();
  }
  const trilha = [
    ['desliza', R1 + 0.04, 'pico', -17], ['boing', TI, 'pico', -15], ['pinos', TI - 0.05, 'inicio', -18], ['assobio', TI + 0.08, 'inicio', -23],
    ['aplauso', TI + 0.35, 'inicio', -21], ['acerto', TI + 1.0, 'pico', -21], ['tonto', TI + TL, 'pico', -18],
  ];
  return { nome: 'Strike', fim: FIM, festa: TI + 0.3, desenhar, trilha, centro: [1150, 560] };
})();

// ================= CENA "KART" (roteiro da seção 5) =================
const kart = (() => {
  const DUR = 3.5, FIM = ST + DUR;
  const TRAS = { y: 700, sc: 0.88 }, FRENTE = { y: 880, sc: 1 };
  function cenario(s) {
    const g = ctx.createLinearGradient(0, 0, 0, 560); g.addColorStop(0, marca.pista[0]); g.addColorStop(1, marca.pista[1]);
    ctx.fillStyle = g; ctx.fillRect(-60, -60, W + 120, 640);
    letreiro();
    // arquibancada com placas da empresa (camada do meio)
    const d1 = (s * 260) % 420;
    ctx.fillStyle = marca.pista[2]; ctx.fillRect(-60, 380, W + 120, 200);
    for (let x = -420 - d1; x < W + 420; x += 420) {
      rr(x + 60, 420, 300, 110, 18); ctx.fillStyle = ROXO; ctx.fill();
      texto(marca.nome, x + 210, 498, 0, 74, '#fff', { familia: 'Francy', ls: 0 });
    }
    // zebra e asfalto (camada rápida)
    const d2 = (s * 900) % 160;
    ctx.fillStyle = '#fff'; ctx.fillRect(-60, 580, W + 120, 34);
    ctx.fillStyle = '#E5203A'; for (let x = -160 - d2; x < W + 160; x += 160) ctx.fillRect(x, 580, 80, 34);
    ctx.fillStyle = '#3B3447'; ctx.fillRect(-60, 614, W + 120, H - 614 + 60);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    for (const y of [790]) for (let x = -160 - d2; x < W + 160; x += 160) ctx.fillRect(x, y, 90, 10);
    ctx.fillStyle = '#fff'; ctx.fillRect(-60, 1010, W + 120, 12);
  }
  // Kart em vista lateral (~300 px). `k` = { p, num, x, y, sc, giro, q (traseira abaixada), empina, derrapa, braco, chama, cara }
  function desenharKart(k) {
    ctx.save(); ctx.translate(k.x, k.y + 2 * Math.sin(k.giro * 3)); ctx.scale(k.sc, k.sc); ctx.rotate(k.derrapa || 0);
    ctx.translate(-110, 0); ctx.rotate(-(k.empina || 0) - (k.q || 0) * 0.5); ctx.translate(110, 0);   // gira em torno da roda de trás
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (k.chama > 0) {   // chama roxa no escapamento
      const L = 90 * k.chama * (0.85 + 0.15 * Math.sin(k.giro * 9));
      ctx.beginPath(); ctx.moveTo(-178, -46); ctx.lineTo(-178 - L, -34); ctx.lineTo(-178, -22); ctx.closePath();
      ctx.fillStyle = VIBRANTE; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = AMARELO; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-178, -41); ctx.lineTo(-178 - L * 0.5, -34); ctx.lineTo(-178, -27); ctx.closePath(); ctx.fillStyle = AMARELO; ctx.fill();
    }
    ctx.lineWidth = 8; ctx.strokeStyle = CONTORNO; ctx.fillStyle = '#9CA3AF'; rr(-182, -50, 44, 22, 8); ctx.fill(); ctx.stroke();   // escapamento
    // tronco e braços no volante (o cabeção vai sentado)
    rr(-52, -150, 92, 100, 32); ctx.fillStyle = VIBRANTE; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.strokeStyle = CONTORNO; ctx.lineWidth = 12; reta(86, -66, 70, -108);   // coluna do volante
    rr(-150, -70, 300, 56, 26); ctx.fillStyle = k.p.cor; ctx.fill(); ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO; ctx.stroke();   // chassi
    rr(110, -84, 46, 30, 12); ctx.fillStyle = k.p.cor; ctx.fill(); ctx.stroke();                                                    // bico
    // plaquinha lateral com a posição: o objeto-símbolo desta cena
    if (k.num) placa(-20, -42, 0, 1, k.num);
    for (const cx of [-110, 110]) {   // rodas com marcas girando
      ctx.beginPath(); ctx.arc(cx, 0, 34, 0, 7); ctx.fillStyle = ESCURO; ctx.fill(); ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO; ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, 0, 14, 0, 7); ctx.fillStyle = '#E5E7EB'; ctx.fill();
      ctx.strokeStyle = '#E5E7EB'; ctx.lineWidth = 5;
      for (let n = 0; n < 3; n++) { const a = k.giro + n * 2.094; reta(cx + Math.cos(a) * 18, Math.sin(a) * 18, cx + Math.cos(a) * 27, Math.sin(a) * 27); }
    }
    const braco = (ox, oy, ang, comBandeira) => {
      ctx.save(); ctx.translate(ox, oy); ctx.rotate(ang);
      ctx.strokeStyle = CONTORNO; ctx.lineWidth = 30; reta(0, 0, 0, 62);
      ctx.strokeStyle = VIBRANTE; ctx.lineWidth = 20; reta(0, 0, 0, 62);
      if (comBandeira) {   // bandeira quadriculada
        ctx.strokeStyle = CONTORNO; ctx.lineWidth = 8; reta(0, 74, 0, 250);
        for (let i = 0; i < 4; i++) for (let j = 0; j < 3; j++) { ctx.fillStyle = (i + j) % 2 ? '#fff' : ESCURO; ctx.fillRect(4 + i * 26, 172 + j * 26, 26, 26); }
        ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.strokeRect(4, 172, 104, 78);
      }
      ctx.beginPath(); ctx.arc(0, 74, 18, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 5; ctx.strokeStyle = CONTORNO; ctx.stroke();
      ctx.restore();
    };
    const comemora = (k.braco || 0) > 0.05;
    if (!comemora) braco(18, -122, -1.15, false);   // mão no volante
    // cabeça: o anel colorido vira capacete, com uma viseira por cima
    rosto(k.p, -6, -246, 104, 12, 5, k.cara);
    ctx.beginPath(); ctx.arc(-6, -246, 110, Math.PI * 1.12, Math.PI * 1.88); ctx.lineWidth = 22; ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.beginPath(); ctx.arc(-6, -246, 110, Math.PI * 1.14, Math.PI * 1.86); ctx.lineWidth = 13; ctx.strokeStyle = '#fff'; ctx.stroke();
    // comemorando: o braço sobe por fora da cabeça, à frente, com a bandeira
    if (comemora) braco(46, -128, lerp(-1.15, -2.45, k.braco), k.braco > 0.6);
    ctx.restore();
  }
  function placa(x, y, rot, sc, num) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.scale(sc, sc);
    rr(-34, -30, 68, 60, 14); ctx.fillStyle = corDaPosicao(num); ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
    texto(String(num), 1, 15, 800, num > 9 ? 34 : 44, ESCURO, { ls: -0.03 });
    ctx.restore();
  }
  function raio(x, y, a) {
    if (a <= 0) return;
    ctx.save(); ctx.translate(x, y); ctx.scale(0.6 + 0.4 * eBack(a), 0.6 + 0.4 * eBack(a)); ctx.rotate(0.15);
    ctx.beginPath(); for (const [px, py] of [[10, -60], [-34, 6], [-4, 6], [-16, 60], [36, -14], [6, -14]]) ctx.lineTo(px, py); ctx.closePath();
    ctx.fillStyle = AMARELO; ctx.fill(); ctx.lineWidth = 7; ctx.lineJoin = 'round'; ctx.strokeStyle = CONTORNO; ctx.stroke(); ctx.restore();
  }
  // fumacinha do escapamento, em marcha lenta
  const escapamento = (x, y, sc, s) => { for (let n = 0; n < 3; n++) { const vida = ciclo(s * 1.8 + n / 3); nuvem(x - (196 + 70 * vida) * sc, y - (44 + 34 * vida) * sc, 17 * sc, 0.12 + 0.88 * vida, '#DAD7E1', n); } };
  function desenhar(t, A, B, pos) {
    const s = t - ST;
    const turbo = eOut(prog(s, 1.0, 1.6)), k0 = Math.exp(-Math.max(0, s - 1.0) * 8);
    const tremida = s > 1.0 && s < 1.5 ? 12 * k0 * Math.sin((s - 1.0) * 75) : 0;
    ctx.save(); ctx.translate(tremida, tremida * 0.6);
    cenario(s + 0.9 * turbo);
    const giro = s * 26;
    // quem cai: toma a fumaça, derrapa e fica para trás, bem distante de quem passou
    const derrapa = prog(s, 1.45, 2.35), xB = lerp(1150, 540, eInOut(derrapa));
    const balanco = derrapa > 0 && derrapa < 1 ? 0.13 * Math.sin(derrapa * Math.PI * 5) * (1 - derrapa) : s > 2.9 ? 0.025 * Math.sin(s * 42) : 0;   // derrapa; depois, treme de raiva
    const sujo = prog(s, 1.5, 1.9), trocou = s >= 2.4;
    const medo = prog(s, 0.75, 0.95) * (1 - sujo);
    escapamento(xB, TRAS.y, TRAS.sc, s);
    if (derrapa > 0 && derrapa < 1) {   // marcas de pneu no asfalto e fumaça nas rodas
      ctx.save(); ctx.strokeStyle = 'rgba(20,16,26,0.5)'; ctx.lineWidth = 9; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (const dx of [-110, 110]) { ctx.beginPath(); for (let n = 0; n <= 9; n++) ctx.lineTo(xB + dx * TRAS.sc + n * 36, TRAS.y + 28 * TRAS.sc + 8 * Math.sin(n * 1.3 + dx)); ctx.stroke(); }
      ctx.restore();
      for (let n = 0; n < 4; n++) { const v = ciclo(s * 2.4 + n / 4); nuvem(xB + (n % 2 ? 110 : -110) * TRAS.sc + 60 * v, TRAS.y + 8 - 44 * v, 24, v, '#F1F0F5', n); }
    }
    desenharKart({ p: B, num: s < 1.6 ? pos : trocou ? pos + 1 : 0, x: xB, y: TRAS.y, sc: TRAS.sc, giro, derrapa: balanco,
      cara: { olhos: s >= 2.75 ? 'raiva' : sujo > 0.3 ? 'piscando' : 'susto', abre: s >= 2.75 ? prog(s, 2.75, 2.95) : Math.max(medo, sujo), olhar: [0.8, 0.1], queixo: prog(s, 1.0, 1.2) * (1 - sujo), suor: medo, sujo, t } });
    // a tosse: uma baforada sai da boca a cada "cof"
    for (let n = 0; n < 6; n++) { const nasce = 1.95 + n * 0.5, vida = prog(s, nasce, nasce + 0.55); nuvem(xB + (50 + 120 * eOut(vida)) * TRAS.sc, TRAS.y - (165 + 30 * vida) * TRAS.sc, 24, vida, '#BDB9C8', n); }
    balao('cof cof', xB - 180, TRAS.y - 390, t, ST + 1.9, Infinity, CONTORNO);
    // quem sobe: faixa da frente, por cima
    const xA = lerp(650, 1450, turbo), antec = prog(s, 0.6, 1.0) * (1 - prog(s, 1.0, 1.15));
    // depois de passar, fecha e assume a faixa de quem ficou para trás
    const fecha = eInOut(prog(s, 1.6, 2.2)), yA = lerp(FRENTE.y, TRAS.y, fecha), scA = lerp(FRENTE.sc, TRAS.sc, fecha);
    const festa = eOut(prog(s, 2.2, 2.5));
    if (turbo > 0 && turbo < 1) {   // linhas de velocidade
      ctx.save(); ctx.globalAlpha = 0.55 * (1 - turbo); ctx.strokeStyle = '#fff'; ctx.lineCap = 'round'; ctx.lineWidth = 11;
      for (const [dy, L] of [[-250, 260], [-150, 360], [-60, 300]]) reta(xA - 220 - L, FRENTE.y + dy, xA - 220, FRENTE.y + dy);
      ctx.restore();
    }
    // rastro do turbo: nuvens que nascem no escapamento, sobem e se desfazem por cima de quem cai
    for (let n = 0; n < 7; n++) {
      const nasce = 1.0 + n * 0.09, vida = prog(s, nasce, nasce + 1.05);
      const x0 = lerp(650, 1450, eOut(prog(nasce, 1.0, 1.6))) - 190;
      nuvem(x0 - 70 * vida + 30 * Math.sin(n * 2.4), FRENTE.y - 60 - (190 + n * 22) * vida, 60 + 12 * (n % 3), vida, n % 2 ? '#E4E2EA' : '#F3F2F6', n);
    }
    if (s < 1.0) escapamento(xA, FRENTE.y, FRENTE.sc, s);
    const foco = prog(s, 0.4, 0.6) * (1 - prog(s, 2.0, 2.15));
    desenharKart({ p: A, num: s < 1.6 ? pos + 1 : trocou ? pos : 0, x: xA, y: yA, sc: scA, giro: giro * (1 + turbo),
      cara: { olhos: foco > 0 ? 'bravo' : null, abre: foco, olhar: [0.7, 0], oculos: prog(s, 2.25, 2.7), brilho: festa, t },
      q: 0.12 * antec, chama: Math.max(antec, turbo < 1 ? prog(s, 1.0, 1.1) * (1 - turbo) : 0), braco: festa,
      empina: 0.16 * festa * (0.6 + 0.4 * Math.abs(Math.sin((s - 2.2) * 5))) });
    raio(650, FRENTE.y - 470, antec);
    // troca das plaquinhas: saltam dos karts, giram no ar e trocam de dono
    if (s >= 1.6 && s < 2.4) {
      const u = eInOut(prog(s, 1.6, 2.4)), arco = 300 * Math.sin(Math.PI * u);
      const deA = [xA - 20 * scA, yA - 42 * scA], deB = [xB - 20 * TRAS.sc, TRAS.y - 42 * TRAS.sc];
      placa(lerp(deB[0], deA[0], u), lerp(deB[1], deA[1], u) - arco, u * 4 * Math.PI, lerp(TRAS.sc, scA, u) * (1 + 0.5 * Math.sin(Math.PI * u)), pos);
      placa(lerp(deA[0], deB[0], u), lerp(deA[1], deB[1], u) - arco * 0.6, -u * 4 * Math.PI, lerp(scA, TRAS.sc, u), pos + 1);
    }
    impacto('TURBO!', `${A.curto} passou ${B.curto}!`, t, ST + 2.3, 980, 170);
    ctx.restore();
  }
  const trilha = [['motor', ST, 'inicio', -24], ['turbo', ST + 1.25, 'pico', -19], ['acerto', ST + 2.4, 'pico', -21], ['aplauso', ST + 2.35, 'inicio', -21]];
  return { nome: 'Kart', fim: FIM, festa: ST + 2.3, desenhar, trilha, centro: [1050, 620] };
})();

// ================= peças compartilhadas pelas cenas de "sala" =================
function sala(piso = marca.piso) {
  const g = ctx.createLinearGradient(0, 0, 0, CH); g.addColorStop(0, marca.parede[0]); g.addColorStop(1, marca.parede[1]);
  ctx.fillStyle = g; ctx.fillRect(-60, -60, W + 120, CH + 60);
  for (const [x, y, r] of [[330, 170, 260], [1600, 140, 320]]) {
    const rg = ctx.createRadialGradient(x, y, 0, x, y, r); rg.addColorStop(0, 'rgba(255,255,255,0.75)'); rg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = rg; ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
  letreiro();
  ctx.fillStyle = ROXO; ctx.fillRect(-60, CH - 105, W + 120, 30);
  ctx.fillStyle = VIBRANTE; ctx.fillRect(-60, CH - 75, W + 120, 8);
  const y0 = CH - 67, p = ctx.createLinearGradient(0, y0, 0, H); p.addColorStop(0, piso[0]); p.addColorStop(1, piso[1]);
  ctx.fillStyle = p; ctx.fillRect(-60, y0, W + 120, H - y0 + 60);
  ctx.fillStyle = `rgba(${marca.rgbContorno},0.35)`; ctx.fillRect(-60, y0, W + 120, 10);
}
function sombraNoChao(x, w = 115, y = CH + 8) { ctx.save(); ctx.fillStyle = `rgba(${marca.rgbContorno},0.22)`; ctx.beginPath(); ctx.ellipse(x, y, w, 15, 0, 0, 7); ctx.fill(); ctx.restore(); }
function nomeNoChao(txt, x, a = 1, y = CH + 44) {
  if (a <= 0.01) return;
  const s = tamanhoQueCabe(txt, 800, 34, 300, 0), w = largura(txt, 800, s, 0) + 48;
  ctx.save(); ctx.globalAlpha *= a; rr(x - w / 2, y, w, 58, 29); ctx.fillStyle = ROXO; ctx.fill();
  texto(txt, x, y + 41, 800, s, '#fff', { ls: 0.02 }); ctx.restore();
}
function tontura(x, yCabeca, u) {   // estrelinhas girando em volta da cabeça: tonto, nunca machucado
  if (u == null || u < 0) return;
  ctx.save(); ctx.globalAlpha = clamp(u * 4);
  for (let k = 0; k < 3; k++) { const ang = u * 5 + k * 2 * Math.PI / 3; estrela(x + Math.cos(ang) * 120, yCabeca - 128 + Math.sin(ang) * 28, 22 + 4 * Math.sin(ang), ang); }
  ctx.restore();
}
const DE_PE = CH - 155;   // altura do centro do corpo com os pés no chão
const passo = (f, amp = 0.6) => { const sn = Math.sin(f); return { pe: [amp * sn, -amp * sn], br: [0.3 - sn * amp * 1.6, -0.3 + sn * amp * 1.6], pulo: 16 * Math.abs(sn) * (amp / 0.6) }; };
// onde está a mão de um braço (0 = esquerdo, 1 = direito) com o boneco de pé e sem rotação
const mao = (b, lado) => { const ang = b.br[lado], ox = lado ? 50 : -50; return [b.x + ox - 80 * Math.sin(ang), b.cy - 34 + 80 * Math.cos(ang)]; };
function bonecoEscala(b, sc) { ctx.save(); ctx.translate(b.x, b.cy); ctx.scale(sc, sc); ctx.translate(-b.x, -b.cy); boneco(b); ctx.restore(); }
function corda(pontos, cor = '#C8A26B') {
  for (const [larg, c] of [[15, CONTORNO], [8, cor]]) {
    ctx.strokeStyle = c; ctx.lineWidth = larg; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.beginPath();
    ctx.moveTo(pontos[0][0], pontos[0][1]); ctx.quadraticCurveTo(pontos[1][0], pontos[1][1], pontos[2][0], pontos[2][1]); ctx.stroke();
  }
}

// ================= CENA "DANÇA DAS CADEIRAS" (a mais leve) =================
// Quem cai levanta para pegar um café; quem sobe senta no lugar. A plaquinha da posição mora na cadeira.
const cadeiras = (() => {
  const DUR = 3.7, FIM = ST + DUR, XC = 1000, ASSENTO = 118, SENTADO = CH - ASSENTO - 50;
  function cadeira(pos) {
    ctx.save(); ctx.translate(XC, CH); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    for (const [larg, cor] of [[24, CONTORNO], [13, '#C89B5E']]) {
      ctx.strokeStyle = cor; ctx.lineWidth = larg;
      reta(-74, -ASSENTO + 10, -74, -6); reta(74, -ASSENTO + 10, 74, -6); reta(-88, -ASSENTO, -100, -350);
    }
    rr(-104, -ASSENTO - 14, 208, 34, 14); ctx.fillStyle = '#E0B77A'; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
    rr(-140, -372, 76, 150, 22); ctx.fillStyle = '#E0B77A'; ctx.fill(); ctx.stroke();
    medalha(-102, -296, 0, 0.95, false, pos);
    ctx.restore();
  }
  function xicara(x, y, a) {
    if (a <= 0) return;
    ctx.save(); ctx.globalAlpha *= a; ctx.translate(x, y);
    ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.beginPath(); ctx.arc(26, -2, 14, -1.3, 1.3); ctx.stroke();
    rr(-24, -24, 48, 44, 10); ctx.fillStyle = '#fff'; ctx.fill(); ctx.stroke();
    ctx.strokeStyle = `rgba(${marca.rgbContorno},0.45)`; ctx.lineWidth = 5; ctx.lineCap = 'round';
    for (const dx of [-10, 8]) { ctx.beginPath(); ctx.moveTo(dx, -34); ctx.quadraticCurveTo(dx + 10, -48, dx, -60); ctx.stroke(); }
    ctx.restore();
  }
  function cantinhoDoCafe(t) {   // para onde quem cai vai: a mesinha com a cafeteira, e a placa
    ctx.save(); ctx.lineJoin = 'round'; ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO;
    rr(1655, 318, 210, 78, 20); ctx.fillStyle = ROXO; ctx.fill(); texto('CAFÉ', 1760, 374, 800, 44, '#fff', { ls: 0.04 });
    ctx.fillStyle = '#C89B5E'; ctx.fillRect(1650, CH - 170, 18, 170); ctx.fillRect(1832, CH - 170, 18, 170); ctx.strokeRect(1650, CH - 170, 18, 170); ctx.strokeRect(1832, CH - 170, 18, 170);
    rr(1620, CH - 190, 260, 28, 10); ctx.fillStyle = '#E0B77A'; ctx.fill(); ctx.stroke();
    // a cafeteira: base, coluna e topo escuros, com a jarra de vidro no meio e o café pingando
    ctx.fillStyle = ESCURO;
    rr(1792, CH - 372, 44, 182, 10); ctx.fill(); ctx.stroke();
    rr(1690, CH - 390, 152, 48, 14); ctx.fill(); ctx.stroke();
    rr(1684, CH - 214, 158, 24, 8); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(1702, CH - 266, 24, Math.PI * 0.5, Math.PI * 1.5); ctx.stroke();                    // alça da jarra
    ctx.fillStyle = '#5A3320'; ctx.fillRect(1738, CH - 342, 8, 30);
    rr(1702, CH - 318, 82, 104, 14); ctx.fillStyle = '#F6F1FB'; ctx.fill();
    ctx.save(); rr(1702, CH - 318, 82, 104, 14); ctx.clip(); ctx.fillStyle = '#5A3320'; ctx.fillRect(1702, CH - 276 + 3 * Math.sin(t * 2), 82, 70); ctx.restore();
    rr(1702, CH - 318, 82, 104, 14); ctx.stroke();
    disco(1814, CH - 366, 8); ctx.fillStyle = AMARELO; ctx.fill();                                               // luzinha de ligada
    ctx.strokeStyle = `rgba(${marca.rgbContorno},0.4)`; ctx.lineWidth = 5; ctx.lineCap = 'round';                            // vapor
    for (const dx of [-18, 14]) { const o = 7 * Math.sin(t * 3 + dx); ctx.beginPath(); ctx.moveTo(1766 + dx, CH - 398); ctx.quadraticCurveTo(1778 + dx + o, CH - 420, 1766 + dx, CH - 442); ctx.stroke(); }
    ctx.restore();
  }
  function desenhar(t, A, B, pos) {
    const s = t - ST, TI = 1.9;
    let sx = 0; if (s > TI && s < TI + 0.5) sx = 10 * Math.exp(-(s - TI) * 8) * Math.sin((s - TI) * 75);
    ctx.save(); ctx.translate(sx, 0);
    sala(); cantinhoDoCafe(t);
    // quem cai: sentado -> levanta -> busca o café -> volta e fica sem entender
    const b = { p: B, rot: 0, pe: [-1.25, -1.4], br: [0.3, -0.3], x: XC + 6, cy: SENTADO + 3 * Math.sin(t * 3) };
    if (s >= 0.5) {
      const levanta = eOut(prog(s, 0.5, 0.8));
      b.cy = lerp(SENTADO, DE_PE, levanta); b.pe = [lerp(-1.25, 0.06, levanta), lerp(-1.4, -0.06, levanta)];
      if (s < 1.6) { const p = prog(s, 0.7, 1.6), m = passo(s * 14, 0.4); b.x = lerp(XC + 6, 1560, p); if (p > 0 && p < 1) { b.pe = m.pe; b.br = m.br; b.cy -= m.pulo; } }
      else if (s < 2.0) { b.x = 1560; b.br = [0.3, -1.8 + 0.08 * Math.sin(s * 20)]; }   // enchendo a xícara na cafeteira
      else { const p = eInOut(prog(s, 2.0, 2.6)), m = passo(s * 14, 0.35); b.x = lerp(1560, 1380, p); b.br = [0.3, -1.2]; if (p < 1) { b.pe = m.pe; b.cy -= m.pulo; } else { b.cab = 0.12 * Math.sin((s - 2.6) * 3); b.br = [lerp(0.3, 1.0, eOut(prog(s, 2.6, 2.9))), -1.2]; } }
    }
    // quem sobe: espia -> corre -> pula na cadeira -> comemora sentado
    const a = { p: A, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3], x: 420, cy: DE_PE, cab: s < 0.9 ? 0.1 * Math.sin(t * 4) : 0 };
    if (s >= 0.9 && s < 1.55) { const m = passo((s - 0.9) * 24); a.x = lerp(420, XC - 190, prog(s, 0.9, 1.55)); a.pe = m.pe; a.br = m.br; a.rot = 0.12; a.cy = DE_PE - m.pulo; }
    else if (s >= 1.55 && s < TI) { const p = prog(s, 1.55, TI); a.x = lerp(XC - 190, XC + 6, p); a.cy = lerp(DE_PE, SENTADO, p) - 150 * Math.sin(Math.PI * p); a.pe = [lerp(0.06, -1.25, p), lerp(-0.06, -1.4, p)]; a.br = [2.2, -2.2]; a.rot = -0.25 * Math.sin(Math.PI * p); }
    else if (s >= TI) { const u = s - TI, festa = eOut(prog(s, TI + 0.15, TI + 0.4)); a.x = XC + 6; a.cy = SENTADO - 26 * Math.abs(Math.sin(u * 7)) * (u > 0.3 && u < 1.5 ? 1 : 0); a.q = 0.16 * Math.exp(-u * 8) * Math.cos(u * 26); a.pe = [-1.25, -1.4]; a.br = [lerp(0.3, 2.7, festa) + 0.15 * Math.sin(t * 12) * festa, lerp(-0.3, -2.7, festa) - 0.15 * Math.sin(t * 12) * festa]; }
    const voltou = prog(s, 2.6, 2.8), espia = prog(s, 0.35, 0.55) * (1 - prog(s, 1.75, TI));
    b.cara = { olhos: s >= 3.2 ? 'confuso' : voltou > 0 ? 'susto' : null, abre: s >= 3.2 ? prog(s, 3.2, 3.38) : voltou, olhar: [-0.85, 0.2], queixo: eOut(prog(s, 2.65, 2.9)) * (1 - 0.7 * prog(s, 3.1, 3.3)), t };
    a.cara = { olhos: espia > 0 ? 'bravo' : s >= TI + 0.15 ? 'feliz' : null, abre: espia > 0 ? espia : prog(s, TI + 0.15, TI + 0.35), olhar: [0.8, 0.1], brilho: prog(s, TI + 0.15, TI + 0.45), t };
    sombraNoChao(XC, 150); sombraNoChao(b.x); if (s < 1.55) sombraNoChao(a.x);
    // quem cai vai assobiando buscar o café: não viu nada
    for (let n = 0; n < 2; n++) { const v = prog(s, 0.8 + n * 0.3, 1.5 + n * 0.3); if (v > 0 && v < 1) nota(b.x - 150 - 20 * n + 14 * Math.sin(v * 7), b.cy - 330 - 90 * v, 15, Math.sin(Math.PI * v)); }
    poeira(430, CH - 8, prog(s, 0.9, 1.45), -1);          // a arrancada de quem sobe
    cadeira(pos);
    const bNaFrente = s < 0.5;   // enquanto está sentado, quem cai fica sobre a cadeira
    if (!bNaFrente) boneco(b);
    if (bNaFrente) boneco(b);
    if (s >= 1.6) { const [mx, my] = mao(b, 1); xicara(mx + 4, my - 6, prog(s, 1.6, 1.75)); }
    boneco(a);
    balao('!', 520, 420, t, ST + 0.45, ST + 0.9, '#B45309');                    // antecipação: a ideia
    balao('?', b.x + 110, 380, t, ST + 2.65, ST + 3.2, CONTORNO);               // fecho: voltou e não entendeu
    poeira(XC, CH - 8, prog(s, TI, TI + 0.6));                                  // o baque na cadeira
    estouro(XC, CH - ASSENTO - 10, t, ST + TI);
    nomeNoChao(A.curto.toUpperCase(), a.x, prog(s, 0.15, 0.35));
    nomeNoChao(B.curto.toUpperCase(), b.x, s < 0.5 ? 0 : prog(s, 2.6, 2.85));
    impacto('SENTOU!', `${A.curto} passou ${B.curto}!`, t, ST + TI + 0.25, 560, 210);
    ctx.restore();
  }
  const trilha = [['desliza', ST + 1.6, 'pico', -18], ['boing', ST + 1.9, 'pico', -16], ['aplauso', ST + 2.15, 'inicio', -21], ['acerto', ST + 2.35, 'pico', -22]];
  return { nome: 'Dança das cadeiras', fim: FIM, festa: ST + 2.15, desenhar, trilha, centro: [1000, 560] };
})();

// ================= CENA "LAÇO" =================
// Quem sobe laça quem está na frente, dá o puxão e joga para trás; a medalha da posição solta no
// caminho e fica com quem laçou. Quem cai pousa sentado e tonto, lá atrás.
const laco = (() => {
  const DUR = 4.0, FIM = ST + DUR, XA = 780, XB = 1440, XF = 260, PEGA = 1.3, PUXA = 1.65, POUSA = 2.45, SENTADO = CH - 66, COR = '#C8A26B';
  function chapeuDeVaqueiro(b) {   // preso à cabeça de quem laça
    ctx.save(); ctx.translate(b.x, b.cy); ctx.rotate(b.rot || 0); ctx.translate(0, -161 - 96); ctx.rotate(-0.08);
    ctx.lineJoin = 'round'; ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.fillStyle = '#B47A45';
    ctx.beginPath(); ctx.moveTo(-90, 0); ctx.bezierCurveTo(-96, -128, -42, -138, 0, -104); ctx.bezierCurveTo(42, -138, 96, -128, 90, 0); ctx.closePath(); ctx.fill(); ctx.stroke();   // copa
    ctx.fillStyle = ROXO; ctx.beginPath(); ctx.moveTo(-93, -52); ctx.quadraticCurveTo(0, -36, 93, -52); ctx.lineTo(91, -24); ctx.quadraticCurveTo(0, -8, -91, -24); ctx.closePath(); ctx.fill(); ctx.stroke();   // fita
    ctx.fillStyle = '#C48A52'; ctx.beginPath(); ctx.ellipse(0, 0, 186, 32, 0, 0, 7); ctx.fill(); ctx.stroke();   // aba
    ctx.restore();
  }
  const volta = (x, y, rx, ry, rot = 0) => { for (const [larg, c] of [[15, CONTORNO], [8, COR]]) { ctx.strokeStyle = c; ctx.lineWidth = larg; ctx.beginPath(); ctx.ellipse(x, y, rx, ry, rot, 0, 7); ctx.stroke(); } };
  // a mão de um braço levando em conta a inclinação do corpo
  const maoDe = (b, lado) => { const [mx, my] = mao(b, lado), dx = mx - b.x, dy = my - b.cy, c = Math.cos(b.rot || 0), sn = Math.sin(b.rot || 0); return [b.x + dx * c - dy * sn, b.cy + dx * sn + dy * c]; };
  function desenhar(t, A, B, pos) {
    const s = t - ST, firma = eOut(prog(s, PEGA, PUXA)), voo = prog(s, PUXA, POUSA), pousou = s >= POUSA, u = s - POUSA;
    let sx = 0; for (const ti of [PUXA, POUSA]) if (s > ti && s < ti + 0.5) sx += 12 * Math.exp(-(s - ti) * 8) * Math.sin((s - ti) * 75);
    ctx.save(); ctx.translate(sx, 0);
    sala(['#F0D9A8', '#D8B56E']);
    const festa = eOut(prog(s, POUSA + 0.1, POUSA + 0.4));
    // quem sobe: gira o laço, lança, finca o pé e dá o puxão por cima do ombro
    const a = { p: A, x: XA, cy: DE_PE - (pousou && u > 0.3 ? 34 * Math.abs(Math.sin((u - 0.3) * 7.5)) : 0), pe: [0.2, -0.2], medalha: s >= PUXA + 0.7 ? pos : 0,
      rot: s < PEGA ? 0.06 * prog(s, 0.9, PEGA) : s < PUXA ? lerp(0.06, -0.2, firma) : pousou ? -0.2 * (1 - festa) : -0.2 - 0.1 * Math.sin(Math.PI * voo),
      br: [lerp(0.3, 2.7, festa), s < 0.9 ? -2.7 + 0.25 * Math.sin(s * 22) : s < PEGA ? lerp(-2.7, -1.5, prog(s, 0.9, PEGA)) : s < PUXA ? -1.5 : pousou ? lerp(-4.5, -2.7, festa) : lerp(-1.5, -4.5, eInOut(voo))] };
    a.cara = { olhos: pousou ? 'estrela' : 'bravo', abre: pousou ? prog(s, POUSA, POUSA + 0.2) : prog(s, 0.1, 0.3) * (1 - prog(s, POUSA - 0.15, POUSA)), olhar: [0.8, 0], brilho: festa, t };
    // quem cai: leva o susto, é laçado, arrastado um pouco e sai voando para trás
    const susto = eOut(prog(s, 0.7, 0.85));
    const b = { p: B, x: XB, cy: DE_PE + 3 * Math.sin(t * 3), rot: 0, pe: [0.06, -0.06], br: [lerp(0.28, 2.0, susto), lerp(-0.28, -2.0, susto)], medalha: s < PUXA ? pos : 0 };
    let tonto = null;
    if (s >= PEGA && s < PUXA) { b.x = XB - 40 * firma; b.rot = -0.22 * firma; b.br = [1.15, -1.15]; b.pe = [0.3, 0.12]; }
    else if (s >= PUXA && !pousou) {
      b.x = lerp(XB - 40, XF, voo); b.cy = lerp(DE_PE, SENTADO, voo) - 330 * Math.sin(Math.PI * voo);
      b.rot = -0.22 - (4 * Math.PI - 0.22) * (1 - Math.pow(1 - voo, 2));
      b.pe = [0.5 + 0.2 * Math.sin(voo * 16), -0.5 - 0.2 * Math.sin(voo * 16)]; b.br = [1.15, -1.15];
    } else if (pousou) {
      tonto = u; b.x = XF; b.cy = SENTADO; b.q = 0.16 * Math.exp(-u * 8) * Math.cos(u * 26);
      b.rot = 0.07 * Math.sin(u * 6); b.cab = 0.08 * Math.sin(u * 6 + 0.8); b.pe = [-1.3, -1.45]; b.br = [0.9 + 0.08 * Math.sin(u * 5), -0.9 - 0.08 * Math.sin(u * 5)];
    }
    b.cara = pousou ? { olhos: 'tonto', queixo: 0.3, t } : s >= PUXA ? { olhos: 'susto', queixo: 1, t }
      : { olhos: 'susto', abre: prog(s, 0.68, 0.85), olhar: [-0.85, -0.2], queixo: Math.max(susto * 0.6, firma), suor: susto, t };
    sombraNoChao(a.x); sombraNoChao(b.x, pousou ? 150 : 115 * (1 - 0.5 * Math.sin(Math.PI * voo)));
    poeira(XB - 40, CH - 8, prog(s, PUXA, PUXA + 0.55), 1);     // de onde ele foi arrancado
    poeira(XF, CH - 8, prog(s, POUSA, POUSA + 0.6));            // e onde foi parar
    const voando = s >= PUXA && !pousou;   // no voo, passa pela frente de quem laçou
    if (!voando) { boneco(b); tontura(b.x, b.cy - 161, tonto); }
    boneco(a); chapeuDeVaqueiro(a);
    const [hx, hy] = maoDe(a, 1);
    if (s < 0.9) {   // girando acima da cabeça
      const cx = a.x + 70, cy = a.cy - 161 - 290, f = s * 13;
      corda([[hx, hy], [hx + 30, hy - 60], [cx + Math.cos(f) * 110, cy + Math.sin(f) * 30]]);
      volta(cx, cy, 118, 34, 0.12 * Math.sin(f));
    } else if (s < PEGA) {   // a volta viaja até quem está na frente
      const ida = eOut(prog(s, 0.9, PEGA)), lx = lerp(a.x + 70, XB, ida), ly = lerp(a.cy - 451, DE_PE + 14, ida) - 160 * Math.sin(Math.PI * ida);
      corda([[hx, hy], [(hx + lx) / 2, Math.min(hy, ly) - 90], [lx, ly]]);
      volta(lx, ly, lerp(118, 84, ida), lerp(34, 24, ida));
    } else if (!pousou) corda([[hx, hy], [(hx + b.x) / 2, (hy + b.cy) / 2 + 40 * (1 - firma)], [b.x, b.cy + 14]]);   // esticada
    else corda([[a.x - 70, CH + 2], [(a.x + XF) / 2, CH + 26], [XF + 82, SENTADO + 20]]);                           // largada no chão
    if (voando) boneco(b);
    if (s >= PEGA) { ctx.save(); ctx.translate(b.x, b.cy); ctx.rotate(b.rot); volta(0, 14, 82, 21); ctx.restore(); }   // a volta do laço na cintura
    if (s >= PUXA && s < PUXA + 0.7) {   // a medalha solta no puxão e cai no peito de quem laçou
      const m = prog(s, PUXA, PUXA + 0.7);
      medalha(lerp(XB - 40, a.x, m), DE_PE + 22 - 380 * Math.sin(Math.PI * m), m * 6 * Math.PI, 0.95, true, pos);
    }
    estouro(XB - 40, DE_PE + 10, t, ST + PUXA);
    balao('!', XB + 120, 410, t, ST + 0.7, ST + PEGA);
    nomeNoChao(A.curto.toUpperCase(), a.x, prog(s, 0.15, 0.35));
    nomeNoChao(B.curto.toUpperCase(), b.x, s < PUXA ? 1 : prog(s, POUSA + 0.1, POUSA + 0.35));
    impacto('LAÇOU!', `${A.curto} passou ${B.curto}!`, t, ST + POUSA + 0.15, 1330, 250, 190);
    ctx.restore();
  }
  const trilha = [['assobio', ST + 0.9, 'inicio', -24], ['boing', ST + PEGA, 'pico', -17], ['desliza', ST + PUXA, 'pico', -17], ['acerto', ST + PUXA + 0.7, 'pico', -20],
    ['tonto', ST + POUSA, 'pico', -19], ['aplauso', ST + POUSA + 0.15, 'inicio', -21]];
  return { nome: 'Laço', fim: FIM, festa: ST + POUSA + 0.15, desenhar, trilha, centro: [960, 560] };
})();

// ================= CENA "CHAPÉU" (futebol) =================
// Quem sobe chega driblando, encobre quem está na frente, passa correndo e domina a bola do outro
// lado. Quem cai acompanha a bola com os olhos, roda atrás dela e senta, tonto.
const chapeu = (() => {
  const DUR = 3.9, FIM = ST + DUR, XB = 1000, P0 = 600, P1 = 1440, CHUTE = 1.0, DOMINA = 1.8, SENTA = 2.3, SENTADO = CH - 66, RB = 36;
  const NO_CHAO = CH + 6 - RB, NA_CABECA = DE_PE - 161 - 127 - RB;
  function bola(x, y, rot) {
    const gomo = (cx, cy) => { ctx.beginPath(); for (let k = 0; k < 5; k++) { const a = k * 1.2566 - Math.PI / 2; ctx.lineTo(cx + Math.cos(a) * 13, cy + Math.sin(a) * 13); } ctx.closePath(); ctx.fill(); };
    ctx.save(); ctx.translate(x, y); ctx.rotate(rot);
    disco(0, 0, RB); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 6; ctx.strokeStyle = CONTORNO; ctx.stroke();
    ctx.save(); disco(0, 0, RB - 3); ctx.clip(); ctx.fillStyle = CONTORNO; ctx.lineWidth = 4; gomo(0, 0);
    for (let k = 0; k < 5; k++) { const a = k * 1.2566 - Math.PI / 2; reta(Math.cos(a) * 13, Math.sin(a) * 13, Math.cos(a) * 26, Math.sin(a) * 26); gomo(Math.cos(a) * 37, Math.sin(a) * 37); }
    ctx.restore(); ctx.restore();
  }
  function campo() {
    sala(['#9AD67F', '#63B257']);
    ctx.fillStyle = 'rgba(255,255,255,0.75)'; ctx.fillRect(-60, CH + 78, W + 120, 10);   // linha lateral
    ctx.save(); ctx.strokeStyle = 'rgba(255,255,255,0.85)'; ctx.lineWidth = 3;             // o gol, lá no fundo
    for (let x = 1710; x <= 1920; x += 28) reta(x, CH - 320, x, CH - 60);
    for (let y = CH - 320; y <= CH - 60; y += 28) reta(1710, y, 1920, y);
    ctx.lineCap = 'round';
    for (const [larg, cor] of [[24, CONTORNO], [13, '#fff']]) { ctx.strokeStyle = cor; ctx.lineWidth = larg; reta(1710, CH - 60, 1710, CH - 320); reta(1710, CH - 320, 1960, CH - 320); }
    ctx.restore();
  }
  const xDeA = s => (s < 0.75 ? lerp(-220, P0, prog(s, 0, 0.75)) : s < CHUTE + 0.08 ? P0 : lerp(P0, P1, eInOut(prog(s, CHUTE + 0.08, DOMINA - 0.05))));
  function bolaEm(s) {   // [x, y, giro]
    if (s < CHUTE) { const x = xDeA(s) + 88; return [x, NO_CHAO - (s < 0.75 ? 34 * Math.abs(Math.sin(s * 13)) : 0), x / RB]; }
    if (s < DOMINA) { const u = prog(s, CHUTE, DOMINA); return [lerp(P0 + 88, P1, u), lerp(NO_CHAO, NA_CABECA, u) - 430 * Math.sin(Math.PI * u), u * 14]; }
    const u = s - DOMINA; return [P1, NA_CABECA - 70 * Math.exp(-u * 4) * Math.abs(Math.sin(u * 10)) - 10 * Math.abs(Math.sin(u * 5)), 14 + u * 0.6];
  }
  function desenhar(t, A, B, pos) {
    const s = t - ST, [bx, byB, giro] = bolaEm(s), festa = eOut(prog(s, DOMINA + 0.15, DOMINA + 0.45));
    ctx.save();
    campo();
    // quem sobe: dribla, arma o chute, encobre e corre para dominar do outro lado
    const a = { p: A, x: xDeA(s), cy: DE_PE, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3], medalha: s >= DOMINA + 0.6 ? pos : 0 };
    const correndo = s < 0.75 || (s > CHUTE + 0.08 && s < DOMINA - 0.05);
    if (correndo) { const m = passo(s * 24); a.pe = m.pe; a.br = m.br; a.rot = 0.12; a.cy = DE_PE - m.pulo; }
    else if (s < CHUTE + 0.08) {
      const arma = prog(s, 0.75, CHUTE - 0.05), chuta = eOut(prog(s, CHUTE - 0.05, CHUTE + 0.06));
      a.rot = lerp(-0.12 * arma, 0.1, chuta); a.q = 0.07 * arma * (1 - chuta); a.pe = [0.06, lerp(lerp(-0.06, 0.75, arma), -1.15, chuta)]; a.br = [0.9, -0.9];
    } else a.br = [lerp(0.3, 2.7, festa) + 0.15 * Math.sin(t * 12) * festa, lerp(-0.3, -2.7, festa) - 0.15 * Math.sin(t * 12) * festa];
    a.cara = { olhos: s < DOMINA ? 'bravo' : 'feliz', abre: s < DOMINA ? prog(s, 0.1, 0.3) * (1 - prog(s, DOMINA - 0.2, DOMINA)) : prog(s, DOMINA + 0.1, DOMINA + 0.3), olhar: [0.8, 0], brilho: festa, t };
    // quem cai: marca de perto, perde a bola de vista lá em cima, roda atrás dela e senta
    const b = { p: B, x: XB + (s < CHUTE ? 16 * Math.sin(s * 7) : 0), cy: DE_PE, rot: 0, pe: [0.2, -0.2], br: [1.0, -1.0], medalha: s < DOMINA ? pos : 0 };
    const segue = prog(s, CHUTE, CHUTE + 0.12), dir = Math.atan2(byB - (DE_PE - 161), bx - b.x);
    let tonto = null, girando = null;
    if (s >= CHUTE && s < CHUTE + 0.55) { b.cab = clamp((bx - b.x) / 500, -0.35, 0.35) * segue; b.br = [2.2, -2.2]; }
    else if (s >= CHUTE + 0.55 && s < SENTA) { girando = 4 * Math.PI * eOut(prog(s, CHUTE + 0.55, SENTA)); b.br = [1.5, -1.5]; }
    else if (s >= SENTA) {
      const u = s - SENTA, cai = eOut(prog(u, 0, 0.25)); tonto = u;
      b.cy = lerp(DE_PE, SENTADO, cai); b.q = 0.16 * Math.exp(-u * 8) * Math.cos(u * 26) * cai; b.rot = 0.07 * Math.sin(u * 6); b.cab = 0.08 * Math.sin(u * 6 + 0.8);
      b.pe = [lerp(0.2, -1.3, cai), lerp(-0.2, -1.45, cai)]; b.br = [0.55 + 0.1 * Math.sin(u * 5), -0.55 - 0.1 * Math.sin(u * 5)];
    }
    b.cara = tonto != null ? (tonto > 0.9 ? { olhos: 'chorando', abre: prog(tonto, 0.9, 1.1), queixo: 0.35, t } : { olhos: 'tonto', queixo: 0.3, t }) : girando != null ? { olhos: 'tonto', queixo: 0.7, t }
      : s >= CHUTE ? { olhos: 'susto', abre: segue, olhar: [Math.cos(dir), Math.sin(dir)], queixo: eOut(prog(s, CHUTE + 0.1, CHUTE + 0.3)), suor: segue, t }
        : { olhos: 'bravo', abre: prog(s, 0.1, 0.3), olhar: [-0.8, 0.3], t };
    sombraNoChao(b.x, tonto != null ? 150 : 115); sombraNoChao(a.x);
    const altura = clamp((NO_CHAO - byB) / 700);   // a sombra da bola encolhe conforme ela sobe
    ctx.save(); ctx.fillStyle = `rgba(${marca.rgbContorno},0.2)`; ctx.beginPath(); ctx.ellipse(bx, CH + 8, 44 * (1 - 0.6 * altura), 10 * (1 - 0.6 * altura), 0, 0, 7); ctx.fill(); ctx.restore();
    if (girando != null) { ctx.save(); ctx.translate(b.x, 0); ctx.scale(Math.cos(girando) * 0.85 + 0.15 * Math.sign(Math.cos(girando) || 1), 1); ctx.translate(-b.x, 0); boneco(b); ctx.restore(); }   // pião
    else boneco(b);
    tontura(b.x, b.cy - 161, tonto != null && tonto < 0.9 ? tonto : null);
    if (girando != null) for (let n = 0; n < 3; n++) { const v = ciclo((s - CHUTE) * 1.6 + n / 3); nuvem(XB + (n - 1) * 115 * (0.5 + v), CH - 14 - 26 * v, 22, v, '#DDEFD2', n); }
    poeira(P0 - 20, CH - 8, prog(s, CHUTE + 0.08, CHUTE + 0.65), -1);     // a arrancada depois do chute
    poeira(XB, CH - 8, prog(s, SENTA + 0.15, SENTA + 0.75));
    if (s > CHUTE + 0.1 && s < DOMINA) {   // linhas de velocidade
      ctx.save(); ctx.globalAlpha = 0.5; ctx.strokeStyle = VIBRANTE; ctx.lineCap = 'round'; ctx.lineWidth = 11;
      for (const [dy, L] of [[-170, 200], [-70, 290], [30, 250]]) reta(a.x - 160 - L, a.cy + dy, a.x - 160, a.cy + dy);
      ctx.restore();
    }
    boneco(a);
    bola(bx, byB, giro);
    if (s >= DOMINA && s < DOMINA + 0.6) {   // a medalha da posição troca de dono
      const m = prog(s, DOMINA, DOMINA + 0.6);
      medalha(lerp(XB, P1, m), DE_PE + 22 - 300 * Math.sin(Math.PI * m), m * 6 * Math.PI, 0.95, true, pos);
    }
    balao('!', XB + 130, 400, t, ST + CHUTE + 0.05, ST + CHUTE + 0.55);
    nomeNoChao(A.curto.toUpperCase(), a.x, prog(s, 0.15, 0.35));
    nomeNoChao(B.curto.toUpperCase(), b.x, 1);
    impacto('CHAPÉU!', `${A.curto} passou ${B.curto}!`, t, ST + DOMINA + 0.15, 500, 220, 180);
    ctx.restore();
  }
  const trilha = [['desliza', ST + CHUTE, 'pico', -18], ['assobio', ST + CHUTE + 0.05, 'inicio', -23], ['boing', ST + DOMINA, 'pico', -17], ['aplauso', ST + DOMINA + 0.2, 'inicio', -21],
    ['acerto', ST + DOMINA + 0.6, 'pico', -21], ['tonto', ST + SENTA + 0.1, 'pico', -19]];
  return { nome: 'Chapéu', fim: FIM, festa: ST + DOMINA + 0.15, desenhar, trilha, centro: [1000, 560] };
})();

// ================= CENA ESPECIAL "COROA" (assumiu o 1º lugar do dia) =================
// Cada um em cima do seu pedestal, com as vendas de hoje no mostrador. Os dois puxam a coroa, de
// igual para igual; entra a venda nova, o pedestal de quem assume sobe um degrau e é isso que
// decide. Depois o trono sobe de dentro do pedestal. Não entra no sorteio: só toca quando a
// liderança do dia muda. `sobe.vendas`, `sobe.antes` e `cai.vendas` alimentam os mostradores.
const coroacao = (() => {
  const DUR = 5.3, FIM = ST + DUR, XA = 540, XB = 900, XC = (XA + XB) / 2, BASE = 200, DEGRAU = 64, ASSENTO = 118, TOPO = 161 + 114;
  const PEGA = 0.55, MAIS = 1.75, SOBE = 1.95, SOLTA = 2.45, POUSA = 2.85, BAQUE = 3.05, TRONO = 3.1, SENTA = 3.7;
  function salao() {
    sala(['#EBDDBE', '#CFB582']);
    ctx.fillStyle = '#C2264B'; ctx.fillRect(-60, CH - 24, W + 120, 96);   // tapete vermelho
    ctx.fillStyle = OURO; ctx.fillRect(-60, CH - 24, W + 120, 9); ctx.fillRect(-60, CH + 63, W + 120, 9);
  }
  function trono(x, base) {
    ctx.save(); ctx.translate(x, base); ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    rr(-136, -560, 272, 470, 64); ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();                          // encosto
    rr(-108, -532, 216, 420, 46); ctx.fillStyle = '#C2264B'; ctx.fill(); ctx.stroke();
    for (const y of [-470, -380, -290]) for (const dx of [-54, 54]) { disco(dx, y, 7); ctx.fillStyle = '#9C1C3B'; ctx.fill(); }   // capitonê
    for (const lado of [-1, 1]) {                                                                           // braços
      rr(lado * 156 - 27, -ASSENTO - 92, 54, 92 + ASSENTO - 6, 18); ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();
      disco(lado * 156, -ASSENTO - 96, 32); ctx.fill(); ctx.stroke();
    }
    rr(-146, -ASSENTO - 18, 292, 44, 16); ctx.fillStyle = '#C2264B'; ctx.fill(); ctx.stroke();              // assento
    rr(-156, -ASSENTO + 24, 312, ASSENTO - 24, 14); ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();         // base
    ctx.restore();
  }
  // Pedestal: coluna de blocos que sai do chão, tampo na cor de quem está em cima e o mostrador das vendas de hoje.
  function pedestal(x, topo, n, cor, pulso = 1) {
    ctx.save(); ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    ctx.save(); ctx.beginPath(); ctx.rect(x - 160, topo, 320, CH + 6 - topo); ctx.clip();
    for (let y = topo + 24; y < CH + DEGRAU; y += DEGRAU) {
      rr(x - 118, y, 236, DEGRAU, 10); ctx.fillStyle = marca.branco; ctx.fill(); ctx.stroke();
      ctx.fillStyle = `rgba(${marca.rgb},0.13)`; ctx.fillRect(x - 112, y + DEGRAU - 18, 224, 12);
    }
    ctx.restore();
    rr(x - 142, topo, 284, 30, 12); ctx.fillStyle = cor; ctx.fill(); ctx.stroke();
    if (n != null) {
      ctx.save(); ctx.translate(x, topo + 82); ctx.scale(pulso, pulso);
      rr(-64, -38, 128, 76, 18); ctx.fillStyle = ESCURO; ctx.fill(); ctx.stroke();
      texto(String(n), 0, 20, 800, 56, AMARELO, { ls: -0.02 }); ctx.restore();
    }
    ctx.restore();
  }
  // ângulo do braço para a mão apontar para um ponto
  const aponta = (b, lado, tx, ty) => Math.atan2(-(tx - (b.x + (lado ? 50 : -50))), ty - (b.cy - 34));
  function desenhar(t, A, B) {
    const s = t - ST, mexe = 0.15 * Math.sin(t * 12);
    // mostradores só quando os números contam a história certa: quem assume passou de quem liderava
    const comNumero = A.vendas != null && B.vendas != null && A.vendas > B.vendas;
    const antes = comNumero ? clamp(A.antes != null ? A.antes : A.vendas - 1, 0, B.vendas) : null;
    const sobe = eBack(prog(s, SOBE, SOBE + 0.4)), topoB = CH - BASE, topoA = CH - BASE - lerp(comNumero && antes < B.vendas ? -1 : 0, 1, sobe) * DEGRAU;
    let sx = 0; for (const ti of [SOBE + 0.3, POUSA]) if (s > ti && s < ti + 0.5) sx += 9 * Math.exp(-(s - ti) * 8) * Math.sin((s - ti) * 75);
    ctx.save(); ctx.translate(sx, 0);
    salao();
    const festa = eOut(prog(s, POUSA, POUSA + 0.3)), u = s - SENTA;
    const agarra = eInOut(prog(s, PEGA - 0.15, PEGA + 0.15)), vai = Math.sin((s - PEGA) * 9), vantagem = prog(s, SOBE + 0.2, SOLTA);
    const dePeA = topoA - 155, dePeB = topoB - 155, sentadoA = topoA - ASSENTO - 50;
    // a coroa: da cabeça de quem lidera para o meio da disputa; depois, para a cabeça de quem assumiu
    const noMeio = [XC + 34 * vai * (1 - vantagem) - 64 * vantagem, (dePeA + dePeB) / 2 + 16];
    const inclina = Math.atan2(dePeB - dePeA, XB - XA) * 0.6;
    const a = { p: A, x: XA, cy: dePeA, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3] };
    const b = { p: B, x: XB, cy: dePeB, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3] };
    let cx, cy, crot, estica = 1;
    if (s < SOLTA) {
      const treme = prog(s, 0.25, PEGA - 0.15) * (1 - agarra);
      cx = lerp(XB + 6 * treme * Math.sin(s * 46), noMeio[0], agarra); cy = lerp(dePeB - TOPO - 12 * treme * Math.abs(Math.sin(s * 24)), noMeio[1], agarra);
      crot = lerp(0.1 * treme * Math.sin(s * 38), inclina + 0.07 * vai, agarra); estica = 1 + 0.12 * agarra * Math.abs(vai);
    } else if (s < POUSA) {
      const v = eInOut(prog(s, SOLTA, POUSA));
      cx = lerp(noMeio[0], XA, v); cy = lerp(noMeio[1], dePeA - TOPO, v) - 150 * Math.sin(Math.PI * v); crot = lerp(inclina, -2 * Math.PI, v);
    }
    // quem assume
    if (s < PEGA - 0.15) { a.cab = -0.1; a.br = [0.3, lerp(-0.3, -2.0, prog(s, 0.2, PEGA - 0.15))]; }
    else if (s < SOLTA) { a.rot = -0.1 * agarra - 0.05 * vai * agarra; a.pe = [0.28, 0.05]; a.br = [aponta(a, 0, cx - 60, cy - 50), aponta(a, 1, cx - 60, cy - 50)]; a.q = 0.1 * Math.sin(Math.PI * prog(s, SOBE + 0.25, SOBE + 0.55)); }
    else if (s < POUSA) { a.rot = lerp(-0.15, 0, prog(s, SOLTA, POUSA)); a.br = [aponta(a, 0, cx, cy - 30), aponta(a, 1, cx, cy - 30)]; }
    else if (s < SENTA - 0.3) { const v = s - POUSA; a.q = 0.14 * Math.exp(-v * 8) * Math.cos(v * 26); a.br = [2.7 + mexe, -2.7 - mexe]; a.cy = dePeA - (v > 0.25 ? 24 * Math.abs(Math.sin((v - 0.25) * 9)) * (1 - prog(s, SENTA - 0.45, SENTA - 0.3)) : 0); }
    else if (s < SENTA) { const v = prog(s, SENTA - 0.3, SENTA); a.cy = lerp(dePeA, sentadoA, v) - 90 * Math.sin(Math.PI * v); a.pe = [lerp(0.06, -1.25, v), lerp(-0.06, -1.4, v)]; a.br = [2.4, -2.4]; }
    else { a.cy = sentadoA - 20 * Math.abs(Math.sin(u * 7)) * (u > 0.3 && u < 1.5 ? 1 : 0); a.q = 0.14 * Math.exp(-u * 8) * Math.cos(u * 26); a.pe = [-1.25, -1.4]; a.br = [2.7 + mexe, -2.7 - mexe]; }
    a.cara = s >= SOLTA ? { olhos: 'estrela', abre: prog(s, SOLTA, SOLTA + 0.2), brilho: festa, t }
      : { olhos: 'bravo', abre: prog(s, 0.1, 0.3), olhar: [0.85, s < PEGA ? -0.5 : 0], suor: agarra, t };
    // quem liderava
    if (s < PEGA - 0.15) { const ve = prog(s, 0.3, PEGA - 0.15); b.cy = dePeB + 3 * Math.sin(t * 3); b.br = [lerp(0.3, 2.6, ve), lerp(-0.3, -2.6, ve)]; }
    else if (s < SOLTA) { b.rot = 0.1 * agarra + 0.05 * vai * agarra; b.pe = [-0.05, -0.28]; b.br = [aponta(b, 0, cx + 60, cy - 50), aponta(b, 1, cx + 60, cy - 50)]; }
    else if (s < BAQUE) {   // a coroa escapa: ele vai para a frente, equilibra na beirada e senta no próprio pedestal
      const v = prog(s, SOLTA, BAQUE), senta = eIn(prog(s, BAQUE - 0.22, BAQUE));
      b.x = XB - 44 * Math.sin(Math.PI * Math.min(1, v * 1.25)) - 14 * senta; b.rot = -0.34 * Math.sin(Math.PI * Math.min(1, v * 1.25)) * (1 - senta);
      b.br = [2.0 + 1.1 * Math.sin(s * 30), -2.0 + 1.1 * Math.sin(s * 30 + 1.4)];
      b.cy = lerp(dePeB, topoB - 66, senta); b.pe = [lerp(0.06, 1.3, senta), lerp(-0.06, 1.45, senta)];
    } else { const v = s - BAQUE; b.x = XB - 14; b.cy = topoB - 66; b.q = 0.16 * Math.exp(-v * 8) * Math.cos(v * 26); b.pe = [1.3, 1.45]; b.br = v > 0.75 ? [0.9 + 0.1 * Math.sin(t * 9), -0.9 - 0.1 * Math.sin(t * 9)] : [0.6, -0.6]; }
    b.cara = s >= BAQUE + 0.75 ? { olhos: 'chorando', abre: prog(s, BAQUE + 0.75, BAQUE + 0.95), queixo: 0.35, t }
      : s >= BAQUE ? { olhos: 'confuso', abre: prog(s, BAQUE, BAQUE + 0.15), t }
        : s >= SOLTA ? { olhos: 'susto', olhar: [-0.8, 0.5], queixo: 1, t }
          : s >= PEGA - 0.15 ? { olhos: 'raiva', abre: agarra, olhar: [-0.85, 0], suor: agarra, t }
            : s >= 0.3 ? { olhos: 'susto', abre: prog(s, 0.3, 0.45), olhar: [-0.85, 0.4], t } : { olhos: 'feliz', t };
    // raios dourados atrás de quem foi coroado
    const raios = prog(s, POUSA, POUSA + 0.35);
    if (raios > 0) {
      const R = 430 * eOut(raios);
      ctx.save(); ctx.translate(a.x, a.cy - 161); ctx.rotate(s * 0.7); ctx.fillStyle = `rgba(242,193,78,${0.6 - 0.25 * prog(s, POUSA + 0.4, POUSA + 1.2)})`;
      for (let k = 0; k < 12; k++) { ctx.rotate(Math.PI / 6); ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(R, -R * 0.11); ctx.lineTo(R, R * 0.11); ctx.closePath(); ctx.fill(); }
      ctx.restore();
    }
    sombraNoChao(XA, 170); sombraNoChao(XB, 170);
    pedestal(XB, topoB, comNumero ? B.vendas : null, B.cor);
    pedestal(XA, topoA, comNumero ? (s < SOBE + 0.15 ? antes : A.vendas) : null, A.cor, 1 + 0.4 * Math.sin(Math.PI * prog(s, SOBE + 0.15, SOBE + 0.5)));
    poeira(XA, CH - 8, prog(s, SOBE, SOBE + 0.6));
    const sai = eBack(prog(s, TRONO, TRONO + 0.45));   // o trono sobe de dentro do pedestal
    if (sai > 0) { ctx.save(); ctx.beginPath(); ctx.rect(0, -200, W, topoA + 202); ctx.clip(); trono(XA, topoA + (1 - sai) * 600); ctx.restore(); }
    boneco(b); boneco(a);
    if (s < POUSA) { ctx.save(); ctx.translate(cx, cy); ctx.rotate(crot); ctx.scale(estica, 1 / Math.sqrt(estica)); coroa(0, 0); ctx.restore(); }
    else { const v = s - POUSA, q = 0.3 * Math.exp(-v * 7) * Math.cos(v * 24); ctx.save(); ctx.translate(a.x, a.cy - TOPO); ctx.scale(1 + q, 1 - q); coroa(0, 0, 1, 0.05 * Math.sin(t * 5) * festa); ctx.restore(); }
    if (s >= PEGA && s < SOLTA) for (const lado of [-1, 1]) {   // tracinhos de esforço em volta da coroa
      ctx.save(); ctx.strokeStyle = CONTORNO; ctx.lineWidth = 7; ctx.lineCap = 'round'; ctx.globalAlpha = 0.5 + 0.5 * Math.abs(vai);
      for (const dy of [-150, -176]) reta(cx + lado * 34, cy + dy, cx + lado * (52 + 14 * Math.abs(vai)), cy + dy - 34);
      ctx.restore();
    }
    balao('+1 venda', XA - 270, topoA + 40, t, ST + MAIS, ST + SOBE + 1.0, '#12925A');
    nomeNoChao(A.curto.toUpperCase(), XA, prog(s, 0.1, 0.3), CH + 84);
    nomeNoChao(B.curto.toUpperCase(), XB, prog(s, 0.1, 0.3), CH + 84);
    impacto('TROCA DE COROA!', `${A.curto} assumiu o 1º lugar de hoje!`, t, ST + SENTA + 0.1, 1475, 290, 104, 790);
    ctx.restore();
  }
  const trilha = [['desliza', ST + PEGA, 'pico', -19], ['acerto', ST + MAIS, 'pico', -18], ['boing', ST + SOBE + 0.1, 'pico', -17], ['assobio', ST + SOLTA, 'inicio', -23],
    ['boing', ST + POUSA, 'pico', -16], ['torcida', ST + POUSA + 0.1, 'inicio', -15], ['desliza', ST + TRONO, 'pico', -19], ['acerto', ST + SENTA, 'pico', -20]];
  return { nome: 'Coroa (1º do dia)', especial: true, titulo: 'NOVA LIDERANÇA!', valendo: 'valendo a coroa de hoje', fim: FIM, festa: ST + POUSA, desenhar, trilha, centro: [760, 520] };
})();

const CENAS = { strike, kart, cadeiras, laco, chapeu, coroa: coroacao };

// ================= MARCO INDIVIDUAL: a torre de força =================
// Marretada no alvo, e o disco sobe pela torre acendendo as faixas da régua. A torre é a META: o
// topo, onde fica o sino, é 100%, e cada faixa abaixo dela tem a sua luz na altura que lhe cabe.
//   - abaixo da meta, o disco para na faixa que a pessoa cruzou; as de cima ficam apagadas, e o
//     sino não toca. O prêmio é uma medalha com a faixa, que sai da luz acesa;
//   - na meta, o disco chega ao topo e bate o sino: troféu e fogos;
//   - acima da meta, o medidor ESTOURA: o disco atravessa o topo, leva o sino junto, e a faixa
//     alcançada aparece no lugar dele. Chove moeda.
// Houve uma versão em que a faixa cruzada era sempre o topo da torre: 80% enchia o medidor igual a
// 100%, e a cena deixava de dizer quanto faltava.
// `info` = { nivel: 1 | 2 | 3, pct, marcos: [faixas até a meta], vendas, meta }
const torre = (() => {
  const DUR = 4.6, FIM = ST + DUR, XA = 880, XP = 668, XT = 500, BATE = 1.2, TOPO = 1.85, PREMIO = 2.55, EMBAIXO = CH - 110, EMCIMA = 262, YSINO = 172;
  const alturaDe = m => lerp(EMBAIXO, EMCIMA, Math.min(m, 100) / 100);
  function sino(x, y, rot) {
    ctx.save(); ctx.translate(x, y - 74); ctx.rotate(rot); ctx.translate(0, 74); ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    disco(0, 36, 15); ctx.fillStyle = '#E0A62A'; ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(-72, 26); ctx.quadraticCurveTo(-52, 14, -50, -22); ctx.bezierCurveTo(-50, -86, 50, -86, 50, -22); ctx.quadraticCurveTo(52, 14, 72, 26); ctx.closePath();
    ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();
    disco(0, -72, 12); ctx.fill(); ctx.stroke();
    ctx.restore();
  }
  function poste(info, yDisco) {
    ctx.save(); ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    rr(XT - 44, 206, 88, CH - 206, 22); ctx.fillStyle = ROXO; ctx.fill(); ctx.stroke();
    rr(XT - 13, 232, 26, EMBAIXO - 232 + 26, 13); ctx.fillStyle = CONTORNO; ctx.fill();
    rr(XT - 96, CH - 54, 192, 54, 14); ctx.fillStyle = VIBRANTE; ctx.fill(); ctx.stroke();
    for (const m of info.marcos) {
      const y = alturaDe(m), ligada = yDisco <= y + 4;
      if (ligada) { disco(XT - 92, y, 36); ctx.fillStyle = 'rgba(255,213,74,0.45)'; ctx.fill(); }
      disco(XT - 92, y, 22); ctx.fillStyle = ligada ? AMARELO : '#CFC6DB'; ctx.fill(); ctx.stroke();
      texto(m === 100 ? 'META' : `${m}%`, XT - 134, y + 15, 800, 42, ligada ? ROXO : `rgba(${marca.rgbContorno},0.4)`, { align: 'right', ls: 0 });
    }
    rr(XT - 25, yDisco - 15, 50, 30, 9); ctx.fillStyle = AMARELO; ctx.fill(); ctx.stroke();   // o disco que sobe
    ctx.restore();
  }
  function marreta(x, y, fi) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(fi); ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    rr(-30, -11, 230, 22, 10); ctx.fillStyle = '#B47A45'; ctx.fill(); ctx.stroke();
    rr(170, -58, 92, 116, 18); ctx.fillStyle = VIBRANTE; ctx.fill(); ctx.stroke();
    ctx.fillStyle = OURO; ctx.fillRect(177, -51, 14, 102); ctx.fillRect(241, -51, 14, 102);
    ctx.restore();
  }
  const aponta = (b, lado, tx, ty) => Math.atan2(-(tx - (b.x + (lado ? 50 : -50))), ty - (b.cy - 34));
  function desenhar(t, A, B, info) {
    const s = t - ST, nivel = info.nivel, mexe = 0.15 * Math.sin(t * 12);
    let sx = 0; for (const ti of [BATE, TOPO]) if (s > ti && s < ti + 0.5) sx += 12 * Math.exp(-(s - ti) * 8) * Math.sin((s - ti) * 75);
    ctx.save(); ctx.translate(sx, 0);
    sala(['#F3DDB4', '#DDBE83']);
    const arma = eInOut(prog(s, 0.65, 1.1)), golpe = eIn(prog(s, 1.1, BATE)), larga = eInOut(prog(s, BATE + 0.2, BATE + 0.5));
    const aperta = s >= BATE ? Math.exp(-(s - BATE) * 9) : 0, festa = eOut(prog(s, TOPO, TOPO + 0.3)), v = s - TOPO, u = s - PREMIO;
    // Até onde o disco vai: a faixa cruzada, ou o topo. Acima da meta ele não para no topo.
    const yAlvo = alturaDe(info.pct), xLuz = XT - 92, voa = v > 0 ? v * v * 2200 + v * 500 : 0;
    const yDisco = nivel === 3 && s > TOPO ? EMCIMA - voa : lerp(EMBAIXO, yAlvo, eOut(prog(s, BATE, TOPO)));
    // quem bateu o marco: chega com a marreta no ombro, arma, bate e fica olhando o disco subir
    const a = { p: A, x: XA, cy: DE_PE, rot: 0, pe: [0.06, -0.06], br: [0.3, -0.3] };
    let mx, my, fi;
    if (s < 0.65) { const m = passo(s * 24); a.x = lerp(W + 240, XA, prog(s, 0, 0.65)); a.pe = m.pe; a.rot = -0.1; a.cy = DE_PE - m.pulo; }
    else if (s < BATE + 0.2) { a.rot = lerp(0.14 * arma, -0.2, golpe); a.q = 0.08 * arma * (1 - golpe); a.pe = [-0.2, 0.26]; }
    else { a.cy = DE_PE - (u > 0 ? 34 * Math.abs(Math.sin(u * 7.5)) : 0); a.br = [lerp(2.2, 2.7, festa) + mexe * festa, lerp(-2.2, -2.7, festa) - mexe * festa]; }
    if (s < BATE + 0.2) {
      fi = s < 1.1 ? lerp(-1.25, -0.45, arma) : lerp(-0.45, -3.76, golpe) + 0.22 * aperta * (s > BATE);
      mx = a.x - 20 - 30 * golpe; my = a.cy - 50 - 70 * arma * (1 - golpe) + 60 * golpe;
      a.br = [aponta(a, 0, mx + Math.cos(fi) * 24, my + Math.sin(fi) * 24), aponta(a, 1, mx + Math.cos(fi) * 70, my + Math.sin(fi) * 70)];
    } else { fi = lerp(-3.76, -Math.PI, larga); mx = lerp(XA - 50, XA - 150, larga); my = lerp(DE_PE + 10, CH - 14, larga); }   // larga a marreta no chão
    const olha = Math.atan2(yDisco - (DE_PE - 161), XT - XA);
    a.cara = s >= TOPO ? { olhos: nivel === 1 ? 'feliz' : nivel === 2 ? 'estrela' : 'cifrao', abre: prog(s, TOPO, TOPO + 0.18), brilho: festa, t }
      : s >= BATE ? { olhos: 'susto', olhar: [Math.cos(olha), Math.sin(olha)], queixo: 0.6, t } : { olhos: 'bravo', abre: prog(s, 0.1, 0.3), olhar: [-0.8, 0.2], suor: arma * (1 - golpe), t };
    if (nivel > 1) queima(s, [[1180, 560, TOPO + 0.2], [1640, 620, TOPO + 0.55], [1420, 500, TOPO + 0.9]]);
    // a torre, o alvo e a alavanca
    ctx.lineWidth = 16; ctx.lineCap = 'round'; ctx.strokeStyle = CONTORNO; reta(XP - 60, CH - 6, XT + 96, CH - 6);
    rr(XP - 76, CH - 34 + 18 * aperta, 152, 34 - 18 * aperta, 10); ctx.fillStyle = '#E5203A'; ctx.fill(); ctx.lineWidth = 7; ctx.stroke();
    poste(info, yDisco);
    if (nivel < 3 || s < TOPO) sino(XT, YSINO, nivel > 1 && s >= TOPO ? 0.42 * Math.exp(-v * 2.5) * Math.sin(v * 18) : 0);   // abaixo da meta o disco não chega nele
    else { const sobe = voa; for (let n = 0; n < 5; n++) { const vida = prog(s, TOPO + n * 0.07, TOPO + 0.8 + n * 0.07); nuvem(XT + 26 * Math.sin(n * 2), YSINO + 40 + 60 * n * vida, 34, vida, '#F1F0F5', n); } sino(XT + 60 * v, YSINO - sobe, v * 9); }   // acima da meta, o sino sai voando
    if (nivel === 3 && v > 0.12) {   // a faixa alcançada, no lugar do sino que voou
      const k = eBack(prog(v, 0.12, 0.42));
      ctx.save(); ctx.translate(XT, YSINO + 4); ctx.scale(k, k); ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
      disco(0, 0, 66); ctx.fillStyle = AMARELO; ctx.fill(); ctx.stroke(); disco(0, 0, 52); ctx.lineWidth = 4; ctx.strokeStyle = `rgba(${marca.rgbContorno},0.35)`; ctx.stroke();
      texto(`${info.pct}%`, 0, 15, 800, 42, ESCURO, { ls: -0.03 }); ctx.restore();
    }
    estouro(nivel === 1 ? xLuz : XT, nivel === 1 ? yAlvo : YSINO, t, ST + TOPO);
    poeira(XP, CH - 8, prog(s, BATE, BATE + 0.6));
    estouro(XP, CH - 40, t, ST + BATE);
    sombraNoChao(a.x);
    if (s < 1.12) { marreta(mx, my, fi); boneco(a); } else { boneco(a); marreta(mx, my, fi); }   // armando o golpe, a marreta passa por trás do rosto
    // o prêmio desce do alto da torre até quem bateu o marco
    if (s >= TOPO + 0.1) {
      const cai = eInOut(prog(s, TOPO + 0.1, PREMIO)), px = lerp(nivel === 1 ? xLuz : XT, a.x, cai), py = lerp(nivel === 1 ? yAlvo : YSINO, a.cy - 161 - 150, cai) - 160 * Math.sin(Math.PI * cai) - (cai >= 1 ? 8 * Math.sin(t * 5) : 0);
      const gira = (1 - cai) * 4 * Math.PI, cresce = 0.4 + 0.6 * cai;
      if (nivel === 1) {
        ctx.save(); ctx.translate(px, py - 62); ctx.rotate(gira); ctx.scale(cresce, cresce); ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
        disco(0, 0, 68); ctx.fillStyle = AMARELO; ctx.fill(); ctx.stroke(); disco(0, 0, 54); ctx.lineWidth = 4; ctx.strokeStyle = `rgba(${marca.rgbContorno},0.35)`; ctx.stroke();
        texto(`${info.pct}%`, 0, 15, 800, 44, ESCURO, { ls: -0.03 }); ctx.restore();
      }
      else trofeu(px, py, (nivel === 3 ? 1.25 : 1.1) * cresce, gira + 0.06 * Math.sin(t * 5) * (cai >= 1));
    }
    if (nivel === 3) moedas(s - TOPO - 0.25);
    nomeNoChao(A.curto.toUpperCase(), a.x, prog(s, 0.5, 0.7));
    impacto(nivel === 2 ? 'META BATIDA!' : `${info.pct}% DA META!`, nivel === 1 ? `${A.curto} entrou na faixa de ${info.pct}%!` : nivel === 2 ? `${A.curto} bateu a meta: ${info.vendas} de ${info.meta}!` : `${A.curto} passou da meta: ${info.vendas} de ${info.meta}!`,
      t, ST + PREMIO + 0.1, 1420, 250, 116, 800);
    ctx.restore();
  }
  const trilha = [['desliza', ST + 1.05, 'pico', -19], ['boing', ST + BATE, 'pico', -15], ['assobio', ST + BATE + 0.05, 'inicio', -23], ['acerto', ST + TOPO, 'pico', -16],
    ['aplauso', ST + TOPO + 0.1, 'inicio', -19], ['acerto', ST + PREMIO, 'pico', -20]];
  return { nome: 'Torre de força', especial: true, fim: FIM, festa: ST + TOPO + 0.1, desenhar, trilha, centro: [760, 560] };
})();

// ================= MARCO DA EQUIPE: meta total e supermeta =================
// O placar vira, o troféu desce no pedestal e a equipe faz a ola. Na supermeta, todo mundo de
// óculos escuros, mais fogos e chuva de moedas. `info` = { super, vendas, meta, equipe: [pessoas] }
const palco = (() => {
  const DUR = 5.0, FIM = ST + DUR, XTR = 1500, YTR = 610, VIRA = 0.8, DESCE = 1.0, POUSA = 1.6, ESC = 0.56;
  // Na supermeta a pose final fica seis segundos na tela (duração 'solene'): os fogos seguem
  // estourando até lá, um a cada 0,7 s, rodando por sete pontos do céu.
  const CEU = [[480, 300], [1640, 560], [230, 470], [1130, 430], [1790, 340], [700, 520], [1320, 250]];
  const FOGOS_QUE_SEGUEM = Array.from({ length: 14 }, (_, k) => [...CEU[k % CEU.length], POUSA + 1.9 + k * 0.7]);
  function desenhar(t, A, B, info) {
    const s = t - ST, gente = info.equipe, n = gente.length, vao = Math.min(200, 1040 / Math.max(1, n - 1)), x0 = 630 - vao * (n - 1) / 2;
    let sx = 0; if (s > POUSA && s < POUSA + 0.5) sx = 12 * Math.exp(-(s - POUSA) * 8) * Math.sin((s - POUSA) * 75);
    ctx.save(); ctx.translate(sx, 0);
    sala(info.super ? ['#F7E2A6', '#E3BC5A'] : marca.palco);
    const u = s - POUSA, desce = eOut(prog(s, DESCE, POUSA)), yTrofeu = lerp(-260, YTR, desce), esc = info.super ? 2.5 : 2.2;
    queima(s, info.super ? [[230, 470, POUSA], [1130, 430, POUSA + 0.3], [1790, 340, POUSA + 0.6], [700, 520, POUSA + 0.9], [1320, 250, POUSA + 1.2], ...FOGOS_QUE_SEGUEM] : [[230, 470, POUSA + 0.1], [1130, 440, POUSA + 0.5], [1790, 340, POUSA + 0.9]]);
    if (u > 0) raiosDourados(XTR, YTR - 80 * esc, (info.super ? 520 : 430) * eOut(prog(u, 0, 0.35)), s * 0.6, 0.5);
    // o pedestal com o placar da equipe
    ctx.lineJoin = 'round'; ctx.lineWidth = 7; ctx.strokeStyle = CONTORNO;
    rr(XTR - 120, YTR + 24, 240, CH - YTR, 12); ctx.fillStyle = marca.branco; ctx.fill(); ctx.stroke();
    rr(XTR - 150, YTR, 300, 30, 12); ctx.fillStyle = OURO; ctx.fill(); ctx.stroke();
    const pulso = 1 + 0.3 * Math.sin(Math.PI * prog(s, VIRA, VIRA + 0.35));
    ctx.save(); ctx.translate(XTR, YTR + 112); ctx.scale(pulso, pulso); rr(-96, -52, 192, 104, 22); ctx.fillStyle = ESCURO; ctx.fill(); ctx.stroke();
    texto(String(s < VIRA ? info.vendas - 1 : info.vendas), 0, 26, 800, 72, AMARELO, { ls: -0.02 }); ctx.restore();
    texto(`de ${info.meta}`, XTR, YTR + 206, 700, 34, CONTORNO, { ls: 0 });
    const q = u > 0 ? 0.2 * Math.exp(-u * 7) * Math.cos(u * 22) : 0;
    ctx.save(); ctx.translate(XTR, yTrofeu); ctx.scale(1 + q, 1 - q); trofeu(0, 0, esc, 0.03 * Math.sin(t * 4) * (u > 0)); ctx.restore();
    // a equipe: olha o troféu descer e depois faz a ola
    gente.forEach((p, i) => {
      const x = x0 + i * vao, onda = u > 0.1 ? Math.max(0, Math.sin(s * 5 - i * 0.7)) : 0;
      const b = { p, x, cy: CH - 155 * ESC - 110 * ESC * onda, rot: 0, pe: [0.06, -0.06], br: [lerp(0.3, 2.7, onda), lerp(-0.3, -2.7, onda)] };
      const para = Math.atan2(yTrofeu - 200 - b.cy, XTR - x);
      b.cara = u > 0.1 ? (info.super ? { oculos: prog(u, 0.2 + i * 0.05, 0.6 + i * 0.05), brilho: 1, t } : { olhos: i % 2 ? 'feliz' : 'estrela', abre: prog(u, 0.1, 0.3), brilho: onda, t })
        : { olhos: 'susto', abre: prog(s, 0.2 + i * 0.05, 0.4 + i * 0.05), olhar: [Math.cos(para), Math.sin(para)], queixo: 0.6 * desce, t };
      ctx.save(); ctx.fillStyle = `rgba(${marca.rgbContorno},0.2)`; ctx.beginPath(); ctx.ellipse(x, CH + 6, 70 * (1 - 0.4 * onda), 10, 0, 0, 7); ctx.fill(); ctx.restore();
      bonecoEscala(b, ESC);
    });
    poeira(XTR, YTR - 4, prog(s, POUSA, POUSA + 0.6));
    if (info.super) { for (const atraso of [0.2, 3.5, 6.8]) moedas(u - atraso, 34); confete(u - 3.3); confete(u - 6.6); }   // a festa recomeça enquanto a pose fica na tela
    impacto(info.super ? 'SUPERMETA!' : 'META BATIDA!', info.super ? `A equipe chegou a ${info.vendas} de ${info.meta}!` : `A equipe fechou ${info.vendas} de ${info.meta}!`, t, ST + POUSA + 0.2, 640, 200, 150, 1000);
    ctx.restore();
  }
  const trilha = [['acerto', ST + VIRA, 'pico', -18], ['assobio', ST + DESCE, 'inicio', -23], ['boing', ST + POUSA, 'pico', -15], ['torcida', ST + POUSA + 0.1, 'inicio', -14], ['acerto', ST + POUSA + 0.9, 'pico', -21]];
  // A torcida dura uns cinco segundos; na pose longa da supermeta ela grita de novo.
  const trilhaDaSupermeta = [...trilha, ['acerto', ST + POUSA + 4.7, 'pico', -19], ['torcida', ST + POUSA + 4.8, 'inicio', -15]];
  return { nome: 'Palco da equipe', especial: true, fim: FIM, festa: ST + POUSA + 0.1, desenhar, trilha, trilhaDaSupermeta, centro: [960, 560] };
})();

// ================= quadro =================
// `tau` é o relógio real; a cena recebe o instante na régua dela.
function render(tau, cena, A, B, pos, d) {
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.filter = 'none'; ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, W, H);
  const t = naCena(tau, d), fim = cena.fim + d.fecho;
  if (t < ST + 0.12) abertura(tau, d, A, B, pos, cena);
  if (t >= ST - IRIS && t < fim + SAIDA) {
    const abre = eInOut(prog(t, ST - IRIS, ST + 0.1));
    ctx.save();
    ctx.beginPath(); ctx.arc(cena.centro[0], cena.centro[1], 1500 * abre, 0, 7); ctx.clip();   // íris que abre
    ctx.translate(0, -H * eIn(prog(t, fim, fim + SAIDA)));
    cena.desenhar(t, A, B, pos);
    confete(t - cena.festa);
    ctx.restore();
    if (abre > 0 && abre < 1) { ctx.beginPath(); ctx.arc(cena.centro[0], cena.centro[1], 1500 * abre, 0, 7); ctx.lineWidth = 18; ctx.strokeStyle = AMARELO; ctx.stroke(); }
  }
}

const pessoa = async p => ({ ...p, curto: String(p.nome).trim().split(/\s+/)[0], cor: p.cor || LILAS, img: await carregar(p.foto) });

// Põe um roteiro no ar: abertura, cena, fecho e saída. Resolve quando a tela foi devolvida ao ranking.
function encenar(canvas, c2d, roteiro, A, B, info, { som, recursos, duracao, chamada, sinal, empresa }, resposta) {
  if (sinal && sinal.aborted) return Promise.resolve(null);
  const d = DURACOES[duracao] || DURACOES.longa;
  const identidade = identidadeDasCenas(empresa);
  const logo = img.get(`${recursos}${identidade.logo}`);
  canvas.width = W; canvas.height = H; ctx = c2d;
  const alvoDoVeu = canvas.dataset.veu ? document.querySelector(canvas.dataset.veu) : canvas.parentElement;
  if (alvoDoVeu) alvoDoVeu.classList.add('ultrapassagem-no-ar');
  canvas.hidden = false;
  // O chamado da abertura é mais alto que o resto: é ele que faz a sala olhar para a tela. Cada evento
  // pode trazer o seu (`chamada`); sem ele, vale o apito.
  const calar = agendarSons(recursos, !som ? [] : [...(chamada ? [] : [['apito', 0.04, 'pico', -13]]), ...(d.duelo ? [['boing', 1.55, 'pico', -17], ['acerto', 1.62, 'pico', -19]] : []),
    ['transicao', real(ST - 0.05, d), 'pico', -22], ...roteiro.trilha.map(([nome, t, alinhamento, db]) => [nome, real(t, d), alinhamento, db]),
    ['transicao', real(roteiro.fim + d.fecho + 0.15, d), 'pico', -22]], chamada);
  return new Promise(resolve => {
    const t0 = performance.now(), total = real(roteiro.fim + d.fecho + SAIDA, d);
    let acabou = false;
    const terminar = () => {
      if (acabou) return; acabou = true;
      clearTimeout(limite);
      c2d.clearRect(0, 0, W, H); canvas.hidden = true;
      if (alvoDoVeu) alvoDoVeu.classList.remove('ultrapassagem-no-ar');
      resolve(resposta);
    };
    // Quem pôs a cena no ar pode tirá-la (a tela foi desmontada, alguém desligou as cenas): ela some
    // e a trilha cala junto. No fim natural o som não é cortado — o aplauso termina sozinho.
    if (sinal) sinal.addEventListener('abort', () => { calar(); terminar(); }, { once: true });
    const quadro = () => {
      if (acabou) return;
      const tau = (performance.now() - t0) / 1000;
      if (tau >= total) { terminar(); return; }
      // Reaplica por quadro: uma prévia de rosto ou um preload de outra empresa não troca a cena.
      aplicarMarca(identidade); logoDaCena = logo;
      ctx = c2d; render(tau, roteiro, A, B, info, d);
      requestAnimationFrame(quadro);
    };
    requestAnimationFrame(quadro);
    // Aba em segundo plano congela o requestAnimationFrame: o ranking não pode ficar preso esperando a cena.
    const limite = setTimeout(terminar, (total + 0.5) * 1000);
  });
}

export async function tocarUltrapassagem(canvas, { sobe, cai, posicao, cena, som = true, recursos = '', duracao = 'longa', chamada = '', sinal, empresa = '' } = {}) {
  const c2d = canvas && canvas.getContext ? canvas.getContext('2d') : null;
  if (!c2d) return;   // sem canvas não há festa, mas o ranking segue
  // Sorteia sem repetir a cena anterior.
  const candidatas = Object.keys(CENAS).filter(n => n !== ultimaCena && !CENAS[n].especial);
  const escolhida = CENAS[cena] ? cena : candidatas[Math.floor(Math.random() * candidatas.length)];
  if (!CENAS[escolhida].especial) ultimaCena = escolhida;
  await preparar(recursos, empresa);
  const [A, B] = await Promise.all([pessoa(sobe), pessoa(cai)]);
  return encenar(canvas, c2d, CENAS[escolhida], A, B, posicao, { som, recursos, duracao, chamada, sinal, empresa }, escolhida);
}

// Monta o roteiro de um marco de meta: a torre para a pessoa, o palco para a equipe.
async function montarMarco({ tipo = 'individual', quem, pct = 100, marcos = [], vendas, meta, equipe = [], supermeta = false, recursos = '', empresa = '' }) {
  await preparar(recursos, empresa);
  if (tipo === 'equipe') {
    const gente = await Promise.all(equipe.slice(0, 8).map(pessoa)), linha = `${vendas} de ${meta}`;
    return { A: null, info: { super: supermeta, vendas, meta, equipe: gente }, roteiro: { ...palco, trilha: supermeta ? palco.trilhaDaSupermeta : palco.trilha, titulo: supermeta ? 'SUPERMETA!' : 'META BATIDA!', apresenta: (tau, V0) => apresentaEquipe(tau, V0, gente, linha) } };
  }
  const A = await pessoa(quem), nivel = pct < 100 ? 1 : pct === 100 ? 2 : 3, linha = `${vendas} de ${meta} vendas`;
  // As luzes da torre: todas as faixas até a meta, cruzadas ou não, e a própria meta no topo. As
  // de cima de 100% não têm luz — passar da meta estoura o medidor.
  const info = { nivel, pct, marcos: [...new Set([...marcos, pct, 100].map(Number).filter(m => m > 0 && m <= 100))].sort((x, y) => x - y), vendas, meta };
  const trilha = nivel === 1 ? torre.trilha : torre.trilha.map(som => (som[0] === 'aplauso' ? ['torcida', som[1], 'inicio', -15] : som));   // da meta para cima, a torcida grita
  return { A, info, roteiro: { ...torre, trilha, titulo: nivel === 2 ? 'META BATIDA!' : `${pct}% DA META!`, apresenta: (tau, V0) => apresentaSolo(tau, V0, A, linha, nivel === 1 ? 'feliz' : nivel === 2 ? 'estrela' : 'cifrao') } };
}
export async function tocarMarco(canvas, opcoes = {}) {
  const c2d = canvas && canvas.getContext ? canvas.getContext('2d') : null;
  if (!c2d) return;
  const { roteiro, A, info } = await montarMarco(opcoes);
  // A supermeta tem a duração dela, qualquer que seja a pedida: nem um ciclo cheio de cenas, que
  // encurta as outras, encurta esta.
  const ehSupermeta = opcoes.tipo === 'equipe' && Boolean(opcoes.supermeta);
  return encenar(canvas, c2d, roteiro, A, null, info, { som: opcoes.som !== false, recursos: opcoes.recursos || '', duracao: ehSupermeta ? 'solene' : opcoes.duracao, chamada: opcoes.chamada, sinal: opcoes.sinal, empresa: opcoes.empresa }, opcoes.tipo || 'individual');
}

export const CENAS_DE_ULTRAPASSAGEM = Object.fromEntries(Object.entries(CENAS).map(([id, c]) => [id, c.nome]));

/** As expressões que o editor de foto mostra na prévia, para conferir os pontos marcados. */
export const EXPRESSOES_DE_PREVIA = [
  ['Susto', { olhos: 'susto', queixo: 1, suor: 1 }],
  ['Feliz', { olhos: 'feliz' }],
  ['Chorando', { olhos: 'chorando', queixo: 0.35 }],
  ['Com raiva', { olhos: 'raiva' }],
  ['Óculos escuros', { oculos: 1 }],
];

/**
 * Desenha um rosto com uma expressão, num canvas qualquer.
 *
 * É a prévia do editor de foto: o mesmo traço que vai para a TV, sobre a imagem que ainda está
 * sendo recortada. `imagem` pode ser um <img> ou outro <canvas>.
 */
export function desenharRosto(canvas, { imagem, pontos, nome = '', cor = LILAS }, cara, t = 0) {
  const c2d = canvas.getContext('2d');
  if (!c2d) return;
  const anterior = ctx, marcaAnterior = marca, lado = Math.min(canvas.width, canvas.height);
  aplicarMarca(MARCAS.mov);
  ctx = c2d;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.clearRect(0, 0, canvas.width, canvas.height);
  // O queixo cai para baixo do círculo: o rosto fica um pouco acima do centro para ele caber.
  rosto({ nome, cor, pontos, img: imagem }, canvas.width / 2, canvas.height * 0.43, lado * 0.33, lado * 0.035, lado * 0.016, { ...cara, t });
  ctx = anterior;
  aplicarMarca(marcaAnterior);
}
